import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCheck, FiX, FiRefreshCw, FiEye, FiEyeOff, FiSearch, FiFilter, FiUser, FiUsers,
  FiShield, FiTrash2, FiToggleLeft, FiArrowLeft, FiClock, FiDollarSign,
  FiTrendingUp, FiCheckCircle, FiPlus, FiEdit, FiCalendar, FiAward,
  FiXCircle, FiInfo, FiChevronDown, FiChevronRight, FiMenu, FiMessageSquare,
  FiActivity, FiPrinter, FiDownload
} from "react-icons/fi";
import axios from "../api/axios";
import MemberTransfer from "./MemberTransfer";
import Invoice from "../components/Invoice";
import { FaArrowUp, FaArrowDown, FaChartBar } from "react-icons/fa";
import { CSVLink } from "react-csv";
import AdminProfileUpdateRequests from "./AdminProfileUpdateRequests";
import ProfileFieldPermissionModal from "../components/ProfileFieldPermissionModal";

/* ─────────────────── STAT CARD ─────────────────── */
const StatCard = ({ title, value, icon, trend, trendValue }) => (
  <div className="relative rounded-2xl shadow-sm p-5 border border-slate-100 hover:shadow-md transition group overflow-hidden bg-white">
    <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20 blur-2xl pointer-events-none bg-gradient-to-br from-blue-500 to-cyan-400" />
    <div className="flex items-center justify-between mb-2">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-tr from-blue-500 to-cyan-400 text-white text-lg shadow group-hover:scale-110 transition-transform">
        {icon ?? <FaChartBar />}
      </span>
      {trend && (
        <span className={`flex items-center text-xs font-semibold ${trend === "up" ? "text-green-600" : "text-red-500"}`}>
          {trend === "up" ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
          {trendValue}
        </span>
      )}
    </div>
    <h4 className="text-xs font-semibold text-slate-500 mb-1 tracking-wide uppercase">{title}</h4>
    <p className="text-2xl font-extrabold text-slate-800">{value ?? "—"}</p>
  </div>
);

/* ─────────────────── SIDEBAR CONFIG ─────────────────── */
const SIDEBAR_BADGE_KEYS = ["kyc", "profile-update-requests", "mobile-store-applications", "new-withdrawals"];

const CountBadge = ({ count, active }) => (count > 0 ? (
  <span
    className={`ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white border-2 ${active ? "border-blue-600" : "border-white"}`}
    style={{ backgroundColor: "#f97316" }}
    aria-label={`${count} new`}
  >
    {count > 99 ? "99+" : count}
  </span>
) : null);

const SIDEBAR = [
  {
    key: "overview",
    label: "Overview",
    icon: <FaChartBar size={14} />,
    children: [],
  },
  {
    key: "all-users",
    label: "All Users",
    icon: <FiUsers size={14} />,
    children: [
      { key: "users-all", label: "All Users Registration" },
      { key: "kyc", label: "KYC" },
      { key: "profile-update-requests", label: "Profile Update Requests" },
      { key: "mobile-store-applications", label: "Mobile Store Applications" },
      { key: "new-withdrawals", label: "Withdrawal" },
      { key: "withdrawals", label: "Withdrawal History" },
    ],
  },
  {
    key: "lbbp-users",
    label: "LBBP Users",
    icon: <FiTrendingUp size={14} />,
    children: [
      { key: "users-lbbp", label: "LBBP Active Users" },
      { key: "shares", label: "GCP Loan Plans" },
      { key: "share-purchases", label: "GCP Loan History" },
      { key: "royalty", label: "Royalty History" },
      { key: "royalty-eligibility", label: "Royalty Eligibility" },
      { key: "new-payments", label: "Add Money" },
      { key: "payments", label: "Add Money History" },
      { key: "fund-transfer", label: "Fund Transfer" },
      { key: "transactions", label: "LBBP Transaction History" },
    ],
  },
  {
    key: "puap-users",
    label: "PU & AP Users",
    icon: <FiUser size={14} />,
    children: [
      { key: "users-puap", label: "PU & AP Active Members" },
      { key: "puap-direct", label: "Direct Payout" },
      { key: "puap-matrix", label: "Matrix Payout" },
      { key: "puap-generation", label: "Generation Payout" },
      { key: "puap-active-member", label: "Active Member Payout" },
      { key: "puap-reward-tour", label: "Reward Tour Bonus Payout" },
      { key: "puap-car-bonus", label: "Car Bonus Payout" },
      { key: "puap-royalty", label: "Royalty Payout" },
      { key: "puap-transactions", label: "PU & AP Transaction History" },
    ],
  },
  {
    key: "add-product",
    label: "Add Product",
    icon: <FiPlus size={14} />,
    children: [
      { key: "products", label: "Product" },
      { key: "banners", label: "Banners" },
      { key: "categories", label: "Category" },
      { key: "product-purchases", label: "Product Purchases" },
    ],
  },
  {
    key: "contact",
    label: "Contact",
    icon: <FiMessageSquare size={14} />,
    children: [
      { key: "contact-list", label: "Contact List" },
      { key: "user-queries", label: "User Queries / Messages" },
      { key: "support-requests", label: "Support Requests" },
    ],
  },
];

/* ─────────────────── MAIN COMPONENT ─────────────────── */
const AdminDashboard = () => {
  const navigate = useNavigate();

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({ "overview": true });
  const [activeTab, setActiveTab] = useState("overview");

  // Data states
  const [data, setData] = useState(null);
  const [kycList, setKycList] = useState([]);
  const [allKycList, setAllKycList] = useState([]);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [kycFilter, setKycFilter] = useState("all");
  const [kycStats, setKycStats] = useState({ total: 0, pending: 0, verified: 0, rejected: 0 });
  const [selectedKYCUsers, setSelectedKYCUsers] = useState([]);
  const [selectAllKYC, setSelectAllKYC] = useState(false);

  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalFilter, setWithdrawalFilter] = useState("all");

  const [shares, setShares] = useState([]);
  const [newShare, setNewShare] = useState({ name: "", price: "", cycleWeeks: "", roiPercentage: "", totalShares: "" });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingShareId, setEditingShareId] = useState(null);
  const [planImageFile, setPlanImageFile] = useState(null);
  const [planImageUploading, setPlanImageUploading] = useState(false);

  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: "", price: "", mrp: "", sp: "", pv: "", quantity: "", category: "", subcategory: "", description: "",
    directCommission: "", matrixCommission: "", generationCommission: "", isActive: true
  });
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImageUploading, setProductImageUploading] = useState(false);

  // Invoice modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const [productPurchases, setProductPurchases] = useState([]);
  const [productPurchasesLoading, setProductPurchasesLoading] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactResponse, setContactResponse] = useState("");
  const [contactStatus, setContactStatus] = useState("");

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [modalImageTitle, setModalImageTitle] = useState("");

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentNote, setPaymentNote] = useState("");
  const [reviewingPaymentId, setReviewingPaymentId] = useState(null);
  const [paymentActionLoading, setPaymentActionLoading] = useState(null);
  const paymentActionLockRef = useRef(new Set());
  const [bulkApproveConfirm, setBulkApproveConfirm] = useState(false);

  const [sharePurchases, setSharePurchases] = useState([]);
  const [sharePurchasesLoading, setSharePurchasesLoading] = useState(false);
  const [sharePurchaseSearch, setSharePurchaseSearch] = useState("");
  const [sharePurchaseStatusFilter, setSharePurchaseStatusFilter] = useState("all");
  const [sharePurchaseDateFilter, setSharePurchaseDateFilter] = useState("all");
  const [sharePurchaseStartDate, setSharePurchaseStartDate] = useState("");
  const [sharePurchaseEndDate, setSharePurchaseEndDate] = useState("");

  const [adminTransactions, setAdminTransactions] = useState([]);
  const [adminTransactionsLoading, setAdminTransactionsLoading] = useState(false);
  const [adminTransactionSearch, setAdminTransactionSearch] = useState("");
  const [adminTransactionTypeFilter, setAdminTransactionTypeFilter] = useState("all");

  const [royaltyRecords, setRoyaltyRecords] = useState([]);
  const [royaltyLoading, setRoyaltyLoading] = useState(false);
  const [royaltySearch, setRoyaltySearch] = useState("");
  const [royaltyMonthFilter, setRoyaltyMonthFilter] = useState("");
  const [royaltyYearFilter, setRoyaltyYearFilter] = useState("");
  const [royaltyStatusFilter, setRoyaltyStatusFilter] = useState("all");
  const [royaltyPagination, setRoyaltyPagination] = useState({ current: 1, total: 0, count: 0 });
  const [royaltySummary, setRoyaltySummary] = useState({ totalAmount: 0, totalRecords: 0, averageAmount: 0, maxAmount: 0, minAmount: 0 });

  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilitySearch, setEligibilitySearch] = useState("");
  const [eligibilityTierFilter, setEligibilityTierFilter] = useState("all");
  const [eligibilityStatusFilter, setEligibilityStatusFilter] = useState("all");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [eligibilitySummary, setEligibilitySummary] = useState({
    totalEligible: 0, totalUsers: 0, totalRoyaltyAmount: 0, goldTier: 0, silverTier: 0, bronzeTier: 0
  });

  const [categories, setCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ name: "", image: "", description: "", isActive: true, order: 0, subcategories: [] });
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [newSubcategory, setNewSubcategory] = useState("");

  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({ title: "", image: "", description: "", isActive: true, order: 0 });
  const [bannerImageFile, setBannerImageFile] = useState(null);
  const [bannerImageUploading, setBannerImageUploading] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionUser, setPermissionUser] = useState(null);
  const [userPanel, setUserPanel] = useState("all");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const togglePasswordVisibility = (userId) => setVisiblePasswords(prev => ({ ...prev, [userId]: !prev[userId] }));

  // Mobile Store Applications states
  const [mobileStoreApps, setMobileStoreApps] = useState([]);
  const [mobileStoreLoading, setMobileStoreLoading] = useState(false);
  const [mobileStoreFilter, setMobileStoreFilter] = useState("all");
  const [selectedMobileStoreApp, setSelectedMobileStoreApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  // PU&AP Payout states
  const [puapDirectPayouts, setPuapDirectPayouts] = useState([]);
  const [puapDirectLoading, setPuapDirectLoading] = useState(false);
  const [puapDirectSearch, setPuapDirectSearch] = useState("");
  const [puapMatrixPayouts, setPuapMatrixPayouts] = useState([]);
  const [puapMatrixLoading, setPuapMatrixLoading] = useState(false);
  const [puapGenerationPayouts, setPuapGenerationPayouts] = useState([]);
  const [puapGenerationLoading, setPuapGenerationLoading] = useState(false);
  const [puapActiveMemberPayouts, setPuapActiveMemberPayouts] = useState([]);
  const [puapActiveMemberLoading, setPuapActiveMemberLoading] = useState(false);
  const [puapRewardTourPayouts, setPuapRewardTourPayouts] = useState([]);
  const [puapRewardTourLoading, setPuapRewardTourLoading] = useState(false);
  const [puapCarBonusPayouts, setPuapCarBonusPayouts] = useState([]);
  const [puapCarBonusLoading, setPuapCarBonusLoading] = useState(false);
  const [puapRoyaltyPayouts, setPuapRoyaltyPayouts] = useState([]);
  const [puapRoyaltyLoading, setPuapRoyaltyLoading] = useState(false);

  /* ─── Sidebar notification counts ─── */
  const USERS_SEEN_KEY = "abmAdminUsersSeenAt";
  const [sidebarCounts, setSidebarCounts] = useState({});

  const refreshSidebarCounts = useCallback(async () => {
    try {
      let seenAt = localStorage.getItem(USERS_SEEN_KEY);
      if (!seenAt) {
        seenAt = new Date().toISOString();
        localStorage.setItem(USERS_SEEN_KEY, seenAt);
      }
      const res = await axios.get("/admin/sidebar-counts", { params: { usersSince: seenAt } });
      setSidebarCounts(res.data || {});
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refreshSidebarCounts();
    const timer = setInterval(() => {
      if (!document.hidden) refreshSidebarCounts();
    }, 30000);
    const onVisible = () => { if (!document.hidden) refreshSidebarCounts(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshSidebarCounts]);

  /* Refresh badge counts once a KYC / mobile-store / withdrawal list is reloaded after an action */
  useEffect(() => {
    if (kycList.length || mobileStoreApps.length || withdrawals.length) refreshSidebarCounts();
  }, [kycList, mobileStoreApps, withdrawals, refreshSidebarCounts]);

  /* ─── Fetch helpers ─── */
  useEffect(() => {
    axios.get("/admin/dashboard").then(r => setData(r.data)).catch(console.error);
  }, []);

  const getTransactionTypeStyle = (type) => {
    if (!type) return { bg: "bg-slate-100", text: "text-slate-700" };
    const typeMap = {
      referral_bonus: { bg: "bg-emerald-100", text: "text-emerald-700" },
      level_income: { bg: "bg-teal-100", text: "text-teal-700" },
      daily_income: { bg: "bg-cyan-100", text: "text-cyan-700" },
      royalty_income: { bg: "bg-sky-100", text: "text-sky-700" },
      bonus: { bg: "bg-green-100", text: "text-green-700" },
      cashback: { bg: "bg-lime-100", text: "text-lime-700" },
      withdrawal: { bg: "bg-rose-100", text: "text-rose-700" },
      transfer: { bg: "bg-orange-100", text: "text-orange-700" },
      purchase: { bg: "bg-amber-100", text: "text-amber-700" },
      fee: { bg: "bg-red-100", text: "text-red-700" },
    };
    return typeMap[type.toLowerCase()] || { bg: "bg-blue-100", text: "text-blue-700" };
  };

  const fetchKYC = async () => {
    try {
      const res = await axios.get("/admin/kyc/all");
      const allKyc = res.data.users;
      setAllKycList(allKyc);
      setKycStats({
        total: allKyc.length,
        pending: allKyc.filter(u => u.kycStatus === "pending").length,
        verified: allKyc.filter(u => u.kycStatus === "verified").length,
        rejected: allKyc.filter(u => u.kycStatus === "rejected").length,
      });
      setKycList(kycFilter === "all" ? allKyc : allKyc.filter(u => u.kycStatus === kycFilter));
    } catch (e) { console.error(e); }
  };

  const fetchWithdrawals = async () => {
    try { const res = await axios.get("/admin/withdrawals"); setWithdrawals(res.data); } catch (e) { console.error(e); }
  };
  const fetchWithdrawalsPendings = async () => {
    try { const res = await axios.get("/admin/withdrawalpendings"); setWithdrawals(res.data); } catch (e) { console.error(e); }
  };
  const fetchShares = async () => {
    try { const res = await axios.get("/share/plans"); setShares(res.data); } catch (e) { console.error(e); }
  };
  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try { const res = await axios.get("/payment/admin/all"); setPayments(res.data || []); }
    catch (e) { console.error(e); } finally { setPaymentsLoading(false); }
  };
  const fetchContacts = async () => {
    try { const res = await axios.get("/contact"); setContacts(res.data.data); } catch (e) { console.error(e); }
  };
  const fetchSharePurchases = async () => {
    setSharePurchasesLoading(true);
    try { const res = await axios.get("/admin/share-purchases"); setSharePurchases(res.data || []); }
    catch (e) { console.error(e); } finally { setSharePurchasesLoading(false); }
  };
  const fetchAdminTransactions = async () => {
    setAdminTransactionsLoading(true);
    try { const res = await axios.get("/admin/transactions"); setAdminTransactions(res.data || []); }
    catch (e) { console.error(e); } finally { setAdminTransactionsLoading(false); }
  };
  const fetchMobileStoreApplications = async () => {
    setMobileStoreLoading(true);
    try { 
      const res = await axios.get("/auth/admin/mobile-store-applications"); 
      setMobileStoreApps(res.data.applications || []); 
    } catch (e) { console.error(e); } finally { setMobileStoreLoading(false); }
  };

  const handleApproveMobileStore = async (applicationId) => {
    try {
      await axios.post(`/auth/admin/mobile-store-applications/${applicationId}/approve`);
      alert("Mobile Store application approved successfully!");
      fetchMobileStoreApplications();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to approve application");
    }
  };

  const handleRejectMobileStore = async () => {
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }
    try {
      await axios.post(`/auth/admin/mobile-store-applications/${selectedMobileStoreApp._id}/reject`, {
        rejectionReason
      });
      alert("Mobile Store application rejected successfully!");
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedMobileStoreApp(null);
      fetchMobileStoreApplications();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to reject application");
    }
  };

  const fetchPuapDirectPayouts = async () => {
    setPuapDirectLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapDirectPayouts((res.data || []).filter(t => t.type === "direct_bonus")); }
    catch (e) { console.error(e); } finally { setPuapDirectLoading(false); }
  };
  const fetchPuapMatrixPayouts = async () => {
    setPuapMatrixLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapMatrixPayouts((res.data || []).filter(t => t.type === "matrix_income")); }
    catch (e) { console.error(e); } finally { setPuapMatrixLoading(false); }
  };
  const fetchPuapGenerationPayouts = async () => {
    setPuapGenerationLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapGenerationPayouts((res.data || []).filter(t => t.type === "generation_income")); }
    catch (e) { console.error(e); } finally { setPuapGenerationLoading(false); }
  };
  const fetchPuapActiveMemberPayouts = async () => {
    setPuapActiveMemberLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapActiveMemberPayouts((res.data || []).filter(t => t.type === "active_member_reward")); }
    catch (e) { console.error(e); } finally { setPuapActiveMemberLoading(false); }
  };
  const fetchPuapRewardTourPayouts = async () => {
    setPuapRewardTourLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapRewardTourPayouts((res.data || []).filter(t => t.type === "reward_tour_bonus")); }
    catch (e) { console.error(e); } finally { setPuapRewardTourLoading(false); }
  };
  const fetchPuapCarBonusPayouts = async () => {
    setPuapCarBonusLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapCarBonusPayouts((res.data || []).filter(t => t.type === "car_bonus")); }
    catch (e) { console.error(e); } finally { setPuapCarBonusLoading(false); }
  };
  const fetchPuapRoyaltyPayouts = async () => {
    setPuapRoyaltyLoading(true);
    try { const res = await axios.get("/admin/transactions"); setPuapRoyaltyPayouts((res.data || []).filter(t => t.type === "royalty_bonus")); }
    catch (e) { console.error(e); } finally { setPuapRoyaltyLoading(false); }
  };
  const fetchRoyaltyRecords = useCallback(async (page = 1) => {
    setRoyaltyLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (royaltyMonthFilter) params.append("month", royaltyMonthFilter);
      if (royaltyYearFilter) params.append("year", royaltyYearFilter);
      if (royaltyStatusFilter && royaltyStatusFilter !== "all") params.append("status", royaltyStatusFilter);
      if (royaltySearch) params.append("userId", royaltySearch);
      const res = await axios.get(`/admin/royalty-records?${params}`);
      setRoyaltyRecords(res.data.records || []);
      setRoyaltyPagination(res.data.pagination || { current: 1, total: 0, count: 0 });
      setRoyaltySummary(res.data.summary || { totalAmount: 0, totalRecords: 0, averageAmount: 0, maxAmount: 0, minAmount: 0 });
    } catch (e) { console.error(e); } finally { setRoyaltyLoading(false); }
  }, [royaltyMonthFilter, royaltyYearFilter, royaltyStatusFilter, royaltySearch]);

  const fetchRoyaltyEligibility = useCallback(async (page = 1) => {
    setEligibilityLoading(true);
    try {
      const res = await axios.get(`/admin/royalty-eligibility?month=${currentMonth}&year=${currentYear}&page=${page}&limit=50`);
      setEligibleUsers(res.data.eligibleUsers || []);
      setEligibilitySummary(res.data.summary || { totalEligible: 0, totalUsers: 0, totalRoyaltyAmount: 0, goldTier: 0, silverTier: 0, bronzeTier: 0 });
    } catch (e) { console.error(e); } finally { setEligibilityLoading(false); }
  }, [currentMonth, currentYear]);

  const fetchUsers = async (panelType = userPanel) => {
    try {
      const query = panelType && panelType !== "all" ? `?panel=${panelType}` : "";
      const res = await axios.get(`/admin/users${query}`);
      setUsers(res.data); setFilteredUsers(res.data); setUserPanel(panelType || "all");
    } catch (e) { console.error(e); }
  };

  const fetchProducts = async () => {
    try { const res = await axios.get("/product/all"); setProducts(res.data.products || []); } catch (e) { console.error(e); }
  };

  const fetchProductPurchases = async () => {
    setProductPurchasesLoading(true);
    try {
      const res = await axios.get("/product/admin/all-purchases");
      setProductPurchases(res.data.purchases || []);
    } catch (e) {
      console.error(e);
    } finally {
      setProductPurchasesLoading(false);
    }
  };

  const fetchCategories = async () => {
    try { const res = await import("../services/categoryService").then(m => m.getCategories()); setCategories(res.data); } catch (e) { }
  };
  const fetchBanners = async () => {
    try { const res = await import("../services/bannerService").then(m => m.getBanners()); setBanners(res.data); } catch (e) { }
  };

  /* ─── Filter users ─── */
  useEffect(() => {
    let filtered = users;
    if (userSearchTerm) {
      const q = userSearchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) ||
        u.mobile?.includes(q) || u.uniqueId?.toLowerCase().includes(q) ||
        u.sponsorDetails?.name?.toLowerCase().includes(q) || u.sponsorDetails?.uniqueId?.toLowerCase().includes(q)
      );
    }
    if (userFilter !== "all") {
      filtered = filtered.filter(u => {
        switch (userFilter) {
          case "with_sponsor": return !!u.sponsorId;
          case "no_sponsor": return !u.sponsorId;
          case "active": return u.status === "active";
          case "inactive": return u.status === "inactive";
          case "suspended": return u.status === "suspended";
          case "verified": return u.kycStatus === "verified";
          case "pending_kyc": return u.kycStatus === "pending";
          case "share_active": return u.isShareActive;
          case "product_active": return u.isProductActive;
          case "admin": return u.role === "admin";
          case "member": return u.role === "member";
          default: return true;
        }
      });
    }
    setFilteredUsers(filtered);
  }, [users, userSearchTerm, userFilter]);

  /* ─── Auto-fetch on tab ─── */
  useEffect(() => {
    if (activeTab === "royalty") {
      const t = setTimeout(() => fetchRoyaltyRecords(1), 500);
      return () => clearTimeout(t);
    }
  }, [activeTab, royaltyMonthFilter, royaltyYearFilter, royaltyStatusFilter, royaltySearch, fetchRoyaltyRecords]);

  useEffect(() => {
    if (activeTab === "royalty-eligibility") {
      const t = setTimeout(() => fetchRoyaltyEligibility(1), 500);
      return () => clearTimeout(t);
    }
  }, [activeTab, currentMonth, currentYear, fetchRoyaltyEligibility]);

  useEffect(() => {
    if (activeTab === "categories") fetchCategories();
    if (activeTab === "banners") fetchBanners();
    if (activeTab === "products") { fetchCategories(); fetchProducts(); }
    if (activeTab === "product-purchases") fetchProductPurchases();
    if (activeTab === "mobile-store-applications") fetchMobileStoreApplications();
  }, [activeTab]);

  /* ─── Sidebar navigation ─── */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedUser(null);
    setSelectedKyc(null);
    setSidebarOpen(false);
    if (tab === "kyc") { setKycFilter("all"); fetchKYC(); }
    if (tab === "withdrawals") fetchWithdrawals();
    if (tab === "new-withdrawals") fetchWithdrawalsPendings();
    if (tab === "shares") fetchShares();
    if (tab === "products") { fetchProducts(); fetchCategories(); }
    if (tab === "contact-list" || tab === "user-queries" || tab === "support-requests") fetchContacts();
    if (tab === "users-all") {
      localStorage.setItem(USERS_SEEN_KEY, new Date().toISOString());
      setSidebarCounts(prev => ({ ...prev, "users-all": 0 }));
      fetchUsers("all");
    } else if (SIDEBAR_BADGE_KEYS.includes(tab)) {
      refreshSidebarCounts();
    }
    if (tab === "users-lbbp") fetchUsers("lbbp");
    if (tab === "users-puap") fetchUsers("puap");
    if (tab === "payments") fetchPayments();
    if (tab === "new-payments") fetchPayments();
    if (tab === "shares" || tab === "share-purchases") fetchSharePurchases();
    if (tab === "transactions") fetchAdminTransactions();
    if (tab === "royalty") fetchRoyaltyRecords();
    if (tab === "royalty-eligibility") fetchRoyaltyEligibility();
    if (tab === "puap-direct") fetchPuapDirectPayouts();
    if (tab === "puap-matrix") fetchPuapMatrixPayouts();
    if (tab === "puap-generation") fetchPuapGenerationPayouts();
    if (tab === "puap-active-member") fetchPuapActiveMemberPayouts();
    if (tab === "puap-reward-tour") fetchPuapRewardTourPayouts();
    if (tab === "puap-car-bonus") fetchPuapCarBonusPayouts();
    if (tab === "puap-royalty") fetchPuapRoyaltyPayouts();
    if (tab === "puap-transactions") fetchAdminTransactions();
    if (tab === "product-purchases") fetchProductPurchases();
  };

  const toggleExpand = (key) => {
    setExpandedKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /* ─── Invoice handlers ─── */
  const handleViewInvoice = async (orderId) => {
    setLoadingInvoice(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `/product/invoice/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setInvoiceData(response.data.invoice);
      setShowInvoiceModal(true);
    } catch (err) {
      alert("Failed to fetch invoice: " + (err.response?.data?.message || err.message));
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  /* ─── KYC handlers ─── */
  const handleApproveKYC = async (userId) => {
    try { await axios.post(`/admin/kyc/approve/${userId}`); fetchKYC(); alert("KYC Approved"); }
    catch { alert("Error approving KYC"); }
  };
  const handleRejectKYC = async (userId) => {
    if (!rejectReason) { alert("Please provide a reason"); return; }
    try {
      await axios.post(`/admin/kyc/reject/${userId}`, { reason: rejectReason });
      fetchKYC(); setRejectReason(""); setSelectedKyc(null); alert("KYC Rejected");
    } catch { alert("Error rejecting KYC"); }
  };
  const handleApproveAllKYC = async () => {
    if (!selectedKYCUsers.length) { alert("Select at least one"); return; }
    if (!window.confirm(`Approve ${selectedKYCUsers.length} KYC requests?`)) return;
    try {
      await Promise.all(selectedKYCUsers.map(id => axios.post(`/admin/kyc/approve/${id}`)));
      fetchKYC(); setSelectedKYCUsers([]); setSelectAllKYC(false);
      alert(`Approved ${selectedKYCUsers.length}`);
    } catch { alert("Error"); }
  };
  const handleRejectAllKYC = async () => {
    if (!selectedKYCUsers.length) { alert("Select at least one"); return; }
    const reason = prompt("Rejection reason:");
    if (!reason) { alert("Required"); return; }
    if (!window.confirm(`Reject ${selectedKYCUsers.length}?`)) return;
    try {
      await Promise.all(selectedKYCUsers.map(id => axios.post(`/admin/kyc/reject/${id}`, { reason })));
      fetchKYC(); setSelectedKYCUsers([]); setSelectAllKYC(false);
      alert(`Rejected ${selectedKYCUsers.length}`);
    } catch { alert("Error"); }
  };

  const filteredKycList = kycFilter === "all" ? allKycList : allKycList.filter(u => u.kycStatus === kycFilter);
  const usersWithKyc = filteredKycList.filter(u => u.kycDocuments && Object.keys(u.kycDocuments).length > 0);

  /* ─── Withdrawal handlers ─── */
  const handleApproveWithdrawal = async (id) => {
    try { await axios.post(`/share/approve-withdrawal/${id}`, { action: "approve" }); fetchWithdrawals(); alert("Approved"); }
    catch { alert("Error"); }
  };
  const handleRejectWithdrawal = async (id) => {
    try { await axios.post(`/share/approve-withdrawal/${id}`, { action: "reject", rejectionReason: "Admin rejected" }); fetchWithdrawals(); alert("Rejected"); }
    catch { alert("Error"); }
  };

  /* ─── Share handlers ─── */
  const handleCreateShare = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = "";
      if (planImageFile) {
        setPlanImageUploading(true);
        const fd = new FormData();
        fd.append("file", planImageFile);
        fd.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
        const up = await (await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: "POST", body: fd })).json();
        setPlanImageUploading(false);
        if (!up.secure_url) { alert("Image upload failed"); return; }
        imageUrl = up.secure_url;
      }
      const payload = { name: newShare.name, price: Number(newShare.price), cycleWeeks: Number(newShare.cycleWeeks), roiPercentage: Number(newShare.roiPercentage), totalShares: newShare.totalShares ? Number(newShare.totalShares) : 0, imageUrl };
      if (editingShareId) await axios.put(`/share/plan/${editingShareId}`, payload);
      else await axios.post("/share/create", payload);
      setNewShare({ name: "", price: "", cycleWeeks: "", roiPercentage: "", totalShares: "" });
      setPlanImageFile(null); setEditingShareId(null); setShowCreateForm(false); fetchShares();
      alert(editingShareId ? "Updated" : "Created");
    } catch { setPlanImageUploading(false); alert("Error"); }
  };
  const handleEditShare = (s) => {
    setEditingShareId(s._id);
    setNewShare({ name: s.name || "", price: String(s.price ?? ""), cycleWeeks: String(s.cycleWeeks ?? Math.ceil((s.cycleDays || 0) / 7)), roiPercentage: String(s.roiPercentage ?? ""), totalShares: String(s.totalShares ?? "") });
    setPlanImageFile(null); setShowCreateForm(true);
  };
  const handleDeleteShare = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await axios.delete(`/share/plan/${id}`); fetchShares(); alert("Deleted"); } catch { alert("Error"); }
  };
  const handleToggleShareStatus = async (s) => {
    try {
      await axios.put(`/share/plan/${s._id}`, { name: s.name, price: Number(s.price), cycleWeeks: Number(s.cycleWeeks ?? Math.ceil((s.cycleDays || 0) / 7)), roiPercentage: Number(s.roiPercentage || 0), isActive: !s.isActive });
      fetchShares(); alert(`${!s.isActive ? "Activated" : "Deactivated"}`);
    } catch { alert("Error"); }
  };
  const handleTogglePurchase = async (s) => {
    try {
      await axios.put(`/share/plan/${s._id}`, { name: s.name, price: Number(s.price), cycleWeeks: Number(s.cycleWeeks ?? Math.ceil((s.cycleDays || 0) / 7)), roiPercentage: Number(s.roiPercentage || 0), isActive: s.isActive, isPurchaseEnabled: !s.isPurchaseEnabled });
      fetchShares();
    } catch { alert("Error"); }
  };
  const handleTogglePayoutMode = async (p) => {
    try {
      await axios.put(`/admin/share-purchases/${p._id}/payout-mode`, { isAutoPayoutEnabled: !(p.isAutoPayoutEnabled !== false) });
      fetchSharePurchases();
    } catch (e) { alert(e.response?.data?.message || "Error"); }
  };
  const handleManualWeeklyCredit = async (p) => {
    if (!window.confirm("Credit weekly payout now?")) return;
    try {
      const res = await axios.post(`/admin/share-purchases/${p._id}/credit-weekly`, { force: true });
      fetchSharePurchases(); alert(`Credited: ₹${Number(res.data?.creditedAmount || 0).toLocaleString()}`);
    } catch (e) { alert(e.response?.data?.message || "Error"); }
  };
  const resetShareForm = () => { setNewShare({ name: "", price: "", cycleWeeks: "", roiPercentage: "", totalShares: "" }); setPlanImageFile(null); setEditingShareId(null); setShowCreateForm(false); };

  /* ─── Product handlers ─── */
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      let productData = { ...newProduct };
      if (productImageFile) {
        setProductImageUploading(true);
        const fd = new FormData();
        fd.append("file", productImageFile);
        fd.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
        const up = await (await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: "POST", body: fd })).json();
        setProductImageUploading(false);
        if (!up.secure_url) { alert("Image upload failed"); return; }
        productData.images = [up.secure_url];
      }
      if (editingProductId) { await axios.put(`/product/update/${editingProductId}`, productData); alert("Updated"); }
      else { await axios.post("/product/create", productData); alert("Created"); }
      resetProductForm(); fetchProducts();
    } catch (e) { setProductImageUploading(false); alert(e.response?.data?.message || "Error"); }
  };
  const handleEditProduct = (p) => {
    setNewProduct({ 
      name: p.name, 
      price: p.price, 
      mrp: p.mrp || p.price, 
      sp: p.sp || p.price, 
      pv: p.pv || p.price, 
      quantity: p.quantity, 
      category: p.category, 
      subcategory: p.subcategory || "", 
      description: p.description, 
      directCommission: p.directCommission, 
      matrixCommission: p.matrixCommission, 
      generationCommission: p.generationCommission, 
      images: p.images || [], 
      isActive: p.isActive 
    });
    setEditingProductId(p._id); setShowProductForm(true);
  };
  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await axios.delete(`/product/delete/${id}`); fetchProducts(); alert("Deleted"); } catch { alert("Error"); }
  };
  const handleToggleProductStatus = async (p) => {
    try { await axios.put(`/product/update/${p._id}`, { ...p, isActive: !p.isActive }); fetchProducts(); } catch { alert("Error"); }
  };
  const resetProductForm = () => { setNewProduct({ name: "", price: "", mrp: "", sp: "", pv: "", quantity: "", category: "", subcategory: "", description: "", images: [], directCommission: "", matrixCommission: "", generationCommission: "", isActive: true }); setProductImageFile(null); setEditingProductId(null); setShowProductForm(false); };

  /* ─── User handlers ─── */
  const handleUpdateUserStatus = async (id, status) => {
    try { await axios.put(`/admin/users/${id}/status`, { status }); fetchUsers(); alert(`Status: ${status}`); } catch { alert("Error"); }
  };
  const handleUpdateUserRole = async (id, role) => {
    try { await axios.put(`/admin/users/${id}/role`, { role }); fetchUsers(); alert(`Role: ${role}`); } catch { alert("Error"); }
  };
  const handleToggleUserActivation = async (id, type) => {
    try {
      await axios.put(`/admin/users/${id}/toggle-${type}`);
      fetchUsers();
    } catch { alert("Error"); }
  };
  const handleDeleteUser = async (id) => {
    if (!window.confirm("Delete user? Cannot be undone.")) return;
    try { await axios.delete(`/admin/users/${id}`); fetchUsers(); setSelectedUser(null); alert("Deleted"); } catch { alert("Error"); }
  };

  /* ─── Payment handlers ─── */
  const handleReviewPayment = async (paymentId, status) => {
    if (paymentActionLockRef.current.has(paymentId)) return;
    paymentActionLockRef.current.add(paymentId);
    setPaymentActionLoading(paymentId);

    try {
      if (status === "approved") {
        const p = payments.find(x => x._id === paymentId);
        if (p) {
          await axios.put(`/payment/admin/${paymentId}`, { status: "approved", adminNote: `₹${p.amount} transferred to LBBP wallet`, walletType: "lbbp" });
          setReviewingPaymentId(null); setPaymentNote(""); fetchPayments();
          alert(`Approved! ₹${p.amount} → LBBP wallet`);
        }
      } else {
        await axios.put(`/payment/admin/${paymentId}`, { status, adminNote: paymentNote });
        setReviewingPaymentId(null); setPaymentNote(""); fetchPayments();
        alert(`Payment ${status}`);
      }
    } catch {
      alert("Error");
    } finally {
      paymentActionLockRef.current.delete(paymentId);
      setPaymentActionLoading(null);
    }
  };

  /* ─── Contact handlers ─── */
  const handleUpdateContact = async (id) => {
    try { await axios.put(`/contact/${id}`, { status: contactStatus, response: contactResponse }); fetchContacts(); setSelectedContact(null); setContactResponse(""); setContactStatus(""); alert("Updated"); }
    catch { alert("Error"); }
  };
  const handleDeleteContact = async (id) => {
    if (!window.confirm("Delete contact?")) return;
    try { await axios.delete(`/contact/${id}`); fetchContacts(); alert("Deleted"); } catch { alert("Error"); }
  };

  /* ─── Image modal ─── */
  const handleOpenImageModal = (url, title = "Screenshot") => { setModalImageUrl(url); setModalImageTitle(title); setImageModalOpen(true); };
  const handleCloseImageModal = () => { setImageModalOpen(false); setModalImageUrl(""); setModalImageTitle(""); };

  /* ─── Category CRUD ─── */
  const handleCategoryFormSubmit = async (e) => {
    e.preventDefault();
    let imageUrl = categoryForm.image;
    if (categoryImageFile) {
      setCategoryImageUploading(true);
      const fd = new FormData();
      fd.append("file", categoryImageFile); fd.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
      const up = await (await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: "POST", body: fd })).json();
      setCategoryImageUploading(false);
      if (!up.secure_url) { alert("Upload failed"); return; }
      imageUrl = up.secure_url;
    }
    try {
      if (editingCategoryId) await import("../services/categoryService").then(m => m.updateCategory(editingCategoryId, { ...categoryForm, image: imageUrl }));
      else await import("../services/categoryService").then(m => m.createCategory({ ...categoryForm, image: imageUrl }));
      setCategoryForm({ name: "", image: "", description: "", isActive: true, order: 0, subcategories: [] });
      setCategoryImageFile(null); setEditingCategoryId(null); fetchCategories();
      alert(editingCategoryId ? "Updated" : "Created");
    } catch { alert("Error"); }
  };
  const handleEditCategory = (c) => { setEditingCategoryId(c._id); setCategoryForm({ name: c.name || "", image: c.image || "", description: c.description || "", isActive: c.isActive, order: c.order || 0, subcategories: c.subcategories || [] }); setCategoryImageFile(null); };
  const handleDeleteCategory = async (id) => { if (!window.confirm("Delete?")) return; try { await import("../services/categoryService").then(m => m.deleteCategory(id)); fetchCategories(); } catch { alert("Error"); } };
  const resetCategoryForm = () => { setCategoryForm({ name: "", image: "", description: "", isActive: true, order: 0, subcategories: [] }); setCategoryImageFile(null); setEditingCategoryId(null); setNewSubcategory(""); };
  const addSubcategory = () => {
    if (newSubcategory.trim()) {
      setCategoryForm(f => ({
        ...f,
        subcategories: [...f.subcategories, { name: newSubcategory.trim(), slug: "", image: "", description: "", isActive: true, order: f.subcategories.length }]
      }));
      setNewSubcategory("");
    }
  };
  const removeSubcategory = (index) => {
    setCategoryForm(f => ({
      ...f,
      subcategories: f.subcategories.filter((_, i) => i !== index)
    }));
  };

  /* ─── Banner CRUD ─── */
  const handleBannerFormSubmit = async (e) => {
    e.preventDefault();
    let imageUrl = bannerForm.image;
    if (bannerImageFile) {
      setBannerImageUploading(true);
      const fd = new FormData();
      fd.append("file", bannerImageFile); fd.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
      const up = await (await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, { method: "POST", body: fd })).json();
      setBannerImageUploading(false);
      if (!up.secure_url) { alert("Upload failed"); return; }
      imageUrl = up.secure_url;
    }
    try {
      if (editingBannerId) await import("../services/bannerService").then(m => m.updateBanner(editingBannerId, { ...bannerForm, image: imageUrl }));
      else await import("../services/bannerService").then(m => m.createBanner({ ...bannerForm, image: imageUrl }));
      setBannerForm({ title: "", image: "", description: "", isActive: true, order: 0 });
      setBannerImageFile(null); setEditingBannerId(null); fetchBanners();
      alert(editingBannerId ? "Updated" : "Created");
    } catch { alert("Error"); }
  };
  const handleEditBanner = (b) => { setEditingBannerId(b._id); setBannerForm({ title: b.title || "", image: b.image || "", description: b.description || "", isActive: b.isActive, order: b.order || 0 }); setBannerImageFile(null); };
  const handleDeleteBanner = async (id) => { if (!window.confirm("Delete?")) return; try { await import("../services/bannerService").then(m => m.deleteBanner(id)); fetchBanners(); } catch { alert("Error"); } };
  const resetBannerForm = () => { setBannerForm({ title: "", image: "", description: "", isActive: true, order: 0 }); setBannerImageFile(null); setEditingBannerId(null); };

  /* ─── Share purchase filters ─── */
  const getFilteredSharePurchases = () => {
    return sharePurchases.filter((p) => {
      const q = sharePurchaseSearch.toLowerCase();
      const matchSearch = !q || p.userId?.name?.toLowerCase().includes(q) || p.userId?.uniqueId?.toLowerCase().includes(q) || p.userId?.email?.toLowerCase().includes(q) || p.userId?.mobile?.includes(q) || p.planId?.name?.toLowerCase().includes(q);
      const matchStatus = sharePurchaseStatusFilter === "all" || p.status === sharePurchaseStatusFilter;
      const purchaseDate = new Date(p.createdAt);
      let matchDate = true;
      if (sharePurchaseDateFilter !== "all") {
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        switch (sharePurchaseDateFilter) {
          case "today": matchDate = purchaseDate >= todayStart; break;
          case "thisMonth": matchDate = purchaseDate >= new Date(today.getFullYear(), today.getMonth(), 1); break;
          case "thisYear": matchDate = purchaseDate >= new Date(today.getFullYear(), 0, 1); break;
          case "custom":
            const s = sharePurchaseStartDate ? new Date(sharePurchaseStartDate) : null;
            let end = sharePurchaseEndDate ? new Date(sharePurchaseEndDate) : null;
            if (end) end.setHours(23, 59, 59, 999);
            if (s && end) matchDate = purchaseDate >= s && purchaseDate <= end;
            else if (s) matchDate = purchaseDate >= s;
            else if (end) matchDate = purchaseDate <= end;
            break;
          default: break;
        }
      }
      return matchSearch && matchStatus && matchDate;
    });
  };

  /* ════════════════════════════════════
     RENDER HELPERS
  ════════════════════════════════════ */

  const renderOverview = () => (
    <div className="p-4 sm:p-6 space-y-8">
      {/* Member Report */}
      <section>
        <h4 className="text-base font-bold text-slate-700 mb-3 pb-2 border-b border-slate-200">Member Report</h4>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="Total Users" value={data?.totalUsers ?? "—"} />
          <StatCard title="Total E-Wallet" value={`₹${(data?.totalEWallet || 0).toLocaleString()}`} />
          <StatCard title="E-Wallet Withdrawal" value={`₹${(data?.totalWithdrawals || 0).toLocaleString()}`} />
          <StatCard title="E-Wallet Pending Withdrawal" value={`₹${(data?.eWalletPendingWithdrawal || 0).toLocaleString()}`} />
        </div>
      </section>
      {/* LBBP Report */}
      <section>
        <h4 className="text-base font-bold text-slate-700 mb-3 pb-2 border-b border-slate-200">LBBP Report</h4>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="Total Add Money" value={`₹${(data?.totalAddMoney || 0).toLocaleString()}`} />
          <StatCard title="LBBP Active Users" value={data?.activeShareUsers ?? "—"} />
          <StatCard title="LBBP Total Loan Amount" value={`₹${(data?.totalInvestment || 0).toLocaleString()}`} />
          <StatCard title="LBBP EMI Payout" value={`₹${(data?.totalROI || 0).toLocaleString()}`} />
          <StatCard title="LBBP Total Level Payout" value={`₹${(data?.totalLevelIncome || 0).toLocaleString()}`} />
          <StatCard title="LBBP Total Royalty Payout" value={`₹${(data?.totalRoyalty || 0).toLocaleString()}`} />
          <StatCard title="LBBP Total Payout" value={`₹${((data?.totalROI || 0) + (data?.totalLevelIncome || 0) + (data?.totalRoyalty || 0)).toLocaleString()}`} />
          <StatCard title="LBBP Wallet Pending Payout" value={`₹${(data?.lbbpWalletPendingPayout || 0).toLocaleString()}`} />
          <StatCard title="Total Transfer to E-Wallet" value={`₹${(data?.totalTransferToEWallet || 0).toLocaleString()}`} />
        </div>
      </section>
      {/* PU & AP Report */}
      <section>
        <h4 className="text-base font-bold text-slate-700 mb-3 pb-2 border-b border-slate-200">PU &amp; AP Report</h4>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="PU & AP Active Users" value={data?.activeProductUsers ?? "—"} />
          <StatCard title="Total Direct Payout" value={`₹${(data?.totalDirectPayout || 0).toLocaleString()}`} />
          <StatCard title="Total Matrix Payout" value={`₹${(data?.totalMatrixPayout || 0).toLocaleString()}`} />
          <StatCard title="Total Generation Payout" value={`₹${(data?.totalGenerationPayout || 0).toLocaleString()}`} />
          <StatCard title="Active Member Reward" value={`₹${(data?.totalActiveMemberReward || 0).toLocaleString()}`} />
          <StatCard title="Reward Tour Bonus" value={`₹${(data?.totalRewardTourBonus || 0).toLocaleString()}`} />
          <StatCard title="Car Bonus Payout" value={`₹${(data?.totalCarBonus || 0).toLocaleString()}`} />
          <StatCard title="Royalty Bonus Payout" value={`₹${(data?.totalPuapRoyalty || 0).toLocaleString()}`} />
          <StatCard title="PU & AP Total Payout" value={`₹${(data?.totalPuapPayout || 0).toLocaleString()}`} />
          <StatCard title="PU & AP Wallet Pending" value={`₹${(data?.puapPendingWithdrawal || 0).toLocaleString()}`} />
          <StatCard title="PU & AP Transfer to E-Wallet" value={`₹${(data?.puapTransferToEWallet || 0).toLocaleString()}`} />
          <StatCard title="PU & AP Total Sales" value={`₹${(data?.puapTotalSales || 0).toLocaleString()}`} />
          <StatCard title="PU & AP Savings" value={`₹${(data?.puapSavings || 0).toLocaleString()}`} />
        </div>
      </section>
    </div>
  );

  const renderUsersTable = (panelLabel) => {
    const getActivationDate = (user) => {
      if (userPanel === "all") return user.createdAt;
      if (userPanel === "lbbp") return user.shareActivationDate;
      if (userPanel === "puap" || userPanel === "puaap") return user.productActivationDate;
      return user.shareActivationDate || user.productActivationDate || user.createdAt || null;
    };

    // Prepare CSV data
    const csvData = filteredUsers.map(user => {
      const activationDate = getActivationDate(user);
      return {
        Name: user.name || '',
        UniqueID: user.uniqueId || '',
        Email: user.email || '',
        Mobile: user.mobile || '',
        Sponsor: user.sponsorDetails?.name || '',
        SponsorID: user.sponsorDetails?.uniqueId || '',
        KYCStatus: user.kycStatus || '',
        Status: user.status || '',
        Role: user.role || '',
        LBBPActive: user.isShareActive ? 'Yes' : 'No',
        PUAPActive: user.isProductActive ? 'Yes' : 'No',
        ActivationDate: activationDate ? new Date(activationDate).toLocaleDateString('en-IN') : '',
        Password: user.password || ''
      };
    });

    const csvHeaders = [
      { label: 'Name', key: 'Name' },
      { label: 'Unique ID', key: 'UniqueID' },
      { label: 'Email', key: 'Email' },
      { label: 'Mobile', key: 'Mobile' },
      { label: 'Sponsor', key: 'Sponsor' },
      { label: 'Sponsor ID', key: 'SponsorID' },
      { label: 'KYC Status', key: 'KYCStatus' },
      { label: 'Status', key: 'Status' },
      { label: 'Role', key: 'Role' },
      { label: 'LBBP Active', key: 'LBBPActive' },
      { label: 'PU&AP Active', key: 'PUAPActive' },
      { label: userPanel === "all" ? 'Joined Date' : 'Activated Date', key: 'ActivationDate' },
      { label: 'Password', key: 'Password' }
    ];

    return (
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="text-lg font-bold text-slate-800">{panelLabel}</h3>
          <div className="flex gap-2">
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`${panelLabel.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm w-fit"
            >
              <FiDownload size={14} /> Export CSV
            </CSVLink>
            <button onClick={() => fetchUsers(userPanel)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm w-fit">
              <FiRefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      {/* Search & Filter */}
      <div className="bg-slate-50 p-3 sm:p-4 rounded-lg mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <input type="text" placeholder="Search name, email, mobile, ID..." value={userSearchTerm} onChange={e => setUserSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
          </div>
          <div className="relative">
            <FiFilter className="absolute left-3 top-3 text-slate-400" />
            <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm appearance-none">
              <option value="all">All Users</option>
              <option value="with_sponsor">With Sponsor</option>
              <option value="no_sponsor">No Sponsor</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="verified">KYC Verified</option>
              <option value="pending_kyc">KYC Pending</option>
              <option value="share_active">Share Active</option>
              <option value="product_active">Product Active</option>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
          </div>
          <div className="flex items-center justify-center bg-white px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-600">
            <FiUsers className="mr-2 text-slate-400" /> {filteredUsers.length} / {users.length}
          </div>
        </div>
      </div>

      {!selectedUser ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs sm:text-sm min-w-[700px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-3 text-left">User</th>
                  <th className="px-3 py-3 text-left">Contact</th>
                  <th className="px-3 py-3 text-left">Sponsor</th>
                  <th className="px-3 py-3 text-left">KYC</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">Role</th>
                  <th className="px-3 py-3 text-left">{userPanel === "all" ? "Joined" : "Activated"}</th>
                  <th className="px-3 py-3 text-left w-[170px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? filteredUsers.map(user => (
                  <tr key={user._id} className="border-b hover:bg-slate-50">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{user.name?.charAt(0).toUpperCase()}</div>
                      <div>
                        <p className="font-semibold text-slate-800">{user.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{user.uniqueId}</p>
                        <p className="text-xs text-slate-500 font-mono flex items-center gap-1">
                          {visiblePasswords[user._id] ? user.password : "••••••••"}
                          <button type="button" onClick={() => togglePasswordVisibility(user._id)} className="text-slate-400 hover:text-slate-600">
                            {visiblePasswords[user._id] ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                          </button>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3"><p>{user.email}</p><p className="text-xs text-slate-500">{user.mobile}</p></td>
                  <td className="px-3 py-3">
                    {user.sponsorDetails ? <div><p className="font-medium">{user.sponsorDetails.name}</p><p className="text-xs text-slate-500">{user.sponsorDetails.uniqueId}</p></div> : <span className="text-slate-400">None</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.kycStatus === "verified" ? "bg-green-100 text-green-800" : user.kycStatus === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>{user.kycStatus}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.isShareActive && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">LBBP</span>}
                      {user.isProductActive && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">PU&AP</span>}
                      {!user.isShareActive && !user.isProductActive && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Inactive</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${user.role === "admin" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>{user.role === "admin" ? "Admin" : "Member"}</span></td>
                  <td className="px-3 py-3 text-xs text-slate-500">{getActivationDate(user) ? new Date(getActivationDate(user)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-wrap gap-2 max-w-[160px]">
                      <button
                        type="button"
                        onClick={() => setSelectedUser(user)}
                        className="h-9 w-9 rounded-lg bg-blue-600 text-white shadow-sm transition hover:bg-blue-700"
                        title="View summary"
                        aria-label="View user summary"
                      >
                        <FiEye className="mx-auto" />
                      </button>
                      <button
                        type="button"
                        onClick={() => window.open(`/user-view?viewUserId=${user._id}`, "_blank")}
                        className="h-9 w-9 rounded-lg bg-purple-600 text-white shadow-sm transition hover:bg-purple-700"
                        title="Open full profile"
                        aria-label="Open full profile"
                      >
                        <FiActivity className="mx-auto" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPermissionUser(user)}
                        className="h-9 w-9 rounded-lg bg-emerald-600 text-white shadow-sm transition hover:bg-emerald-700"
                        title="Manage profile update access"
                        aria-label="Manage profile update access"
                      >
                        <FiEdit className="mx-auto" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateUserStatus(user._id, user.status === "active" ? "inactive" : "active")}
                        className="h-9 w-9 rounded-lg bg-amber-500 text-white shadow-sm transition hover:bg-amber-600"
                        title={user.status === "active" ? "Set inactive" : "Set active"}
                        aria-label={user.status === "active" ? "Set inactive" : "Set active"}
                      >
                        <FiToggleLeft className="mx-auto" />
                      </button>
                    </div>
                  </td>
                </tr>
                )) : (
                  <tr><td colSpan="8" className="px-6 py-8 text-center text-slate-500">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {permissionUser && (
            <ProfileFieldPermissionModal
              user={permissionUser}
              onClose={() => setPermissionUser(null)}
            />
          )}
        </>
      ) : (
        /* User Detail View */
        <div className="bg-slate-50 p-4 sm:p-6 rounded-lg">
          <div className="flex items-center justify-between mb-5">
            <h4 className="text-base font-bold text-slate-800">User: {selectedUser.name}</h4>
            <button onClick={() => setSelectedUser(null)} className="bg-slate-600 text-white px-3 py-1.5 rounded text-sm hover:bg-slate-700">← Back</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Basic Info */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-blue-600">
              <h5 className="font-semibold text-slate-800 mb-3 flex items-center gap-2 text-sm"><FiUser /> Basic Information</h5>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-500">Name:</span> {selectedUser.name}</p>
                <p><span className="text-slate-500">Email:</span> {selectedUser.email}</p>
                <p><span className="text-slate-500">Mobile:</span> {selectedUser.mobile}</p>
                <p><span className="text-slate-500">ID:</span> {selectedUser.uniqueId}</p>
              </div>
            </div>
            {/* Sponsor */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-cyan-600">
              <h5 className="font-semibold text-slate-800 mb-3 flex items-center gap-2 text-sm"><FiUsers /> Sponsor</h5>
              {selectedUser.sponsorDetails ? (
                <div className="space-y-1.5 text-sm">
                  <p><span className="text-slate-500">Name:</span> {selectedUser.sponsorDetails.name}</p>
                  <p><span className="text-slate-500">Email:</span> {selectedUser.sponsorDetails.email}</p>
                  <p><span className="text-slate-500">Mobile:</span> {selectedUser.sponsorDetails.mobile}</p>
                  <p><span className="text-slate-500">ID:</span> {selectedUser.sponsorDetails.uniqueId}</p>
                </div>
              ) : <p className="text-sm text-slate-400">No sponsor</p>}
            </div>
            {/* Status & Role */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-green-600">
              <h5 className="font-semibold text-slate-800 mb-3 flex items-center gap-2 text-sm"><FiShield /> Status & Role</h5>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Account Status</p>
                  <select value={selectedUser.status} onChange={e => handleUpdateUserStatus(selectedUser._id, e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm">
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">User Role</p>
                  <select value={selectedUser.role} onChange={e => handleUpdateUserRole(selectedUser._id, e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm">
                    <option value="member">Member</option><option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Partnership */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-purple-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Partnership</h5>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">LBBP</span>
                  <button onClick={() => handleToggleUserActivation(selectedUser._id, "share")} className={`px-3 py-1 rounded text-xs font-medium ${selectedUser.isShareActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>{selectedUser.isShareActive ? "Active" : "Inactive"}</button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">PU&AP</span>
                  <button onClick={() => handleToggleUserActivation(selectedUser._id, "product")} className={`px-3 py-1 rounded text-xs font-medium ${selectedUser.isProductActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>{selectedUser.isProductActive ? "Active" : "Inactive"}</button>
                </div>
              </div>
            </div>
            {/* KYC */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">KYC</h5>
              <div className="space-y-1.5 text-sm">
                <p>Status: <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${selectedUser.kycStatus === "verified" ? "bg-green-100 text-green-800" : selectedUser.kycStatus === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>{selectedUser.kycStatus}</span></p>
                {selectedUser.kycDocuments && <>
                  <p><span className="text-slate-500">PAN:</span> {selectedUser.kycDocuments.pan || "N/A"}</p>
                  <p><span className="text-slate-500">Bank:</span> {selectedUser.kycDocuments.bankName || "N/A"}</p>
                  <p><span className="text-slate-500">A/C:</span> {selectedUser.kycDocuments.bankAccount || "N/A"}</p>
                  <p><span className="text-slate-500">IFSC:</span> {selectedUser.kycDocuments.ifsc || "N/A"}</p>
                </>}
              </div>
            </div>
            {/* Wallet */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-emerald-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Wallet</h5>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-500">LBBP:</span> ₹{Number(selectedUser.walletSummary?.lbbpWallet || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">PU&AP:</span> ₹{Number(selectedUser.walletSummary?.puapWallet || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">E-Wallet:</span> ₹{Number(selectedUser.walletSummary?.eWallet || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">Total Withdrawn:</span> ₹{Number(selectedUser.walletSummary?.totalWithdrawn || 0).toLocaleString()}</p>
              </div>
            </div>
            {/* Income */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-pink-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Income</h5>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-500">Level:</span> ₹{Number(selectedUser.incomeSummary?.levelIncome || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">EMI:</span> ₹{Number(selectedUser.incomeSummary?.roiIncome || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">Royalty:</span> ₹{Number(selectedUser.incomeSummary?.royaltyIncome || 0).toLocaleString()}</p>
                <p><span className="text-slate-500">Direct Bonus:</span> ₹{Number(selectedUser.incomeSummary?.directBonus || 0).toLocaleString()}</p>
                <p className="font-semibold"><span className="text-slate-500">Total:</span> ₹{Number(selectedUser.incomeSummary?.totalIncome || 0).toLocaleString()}</p>
              </div>
            </div>
            {/* Location */}
            <div className="bg-white p-4 rounded-lg border-l-4 border-indigo-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Location</h5>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-500">State:</span> {selectedUser.state || "N/A"}</p>
                <p><span className="text-slate-500">District:</span> {selectedUser.district || "N/A"}</p>
                <p><span className="text-slate-500">Tahsil:</span> {selectedUser.tahsil || "N/A"}</p>
                <p><span className="text-slate-500">Pin:</span> {selectedUser.pinCode || "N/A"}</p>
              </div>
            </div>
            {/* Danger - disabled for All Users Registration / LBBP Active Users / PU & AP Active Members
            <div className="bg-white p-4 rounded-lg border-l-4 border-red-600">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Danger Zone</h5>
              <button onClick={() => handleDeleteUser(selectedUser._id)} className="w-full bg-red-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-red-700 flex items-center justify-center gap-2"><FiTrash2 /> Delete User</button>
              <p className="text-xs text-slate-500 mt-2">This action cannot be undone</p>
            </div>
            */}
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderPlaceholder = (title) => (
    <div className="p-6 flex flex-col items-center justify-center min-h-64 text-slate-400">
      <FiInfo size={36} className="mb-3 text-slate-300" />
      <p className="text-lg font-semibold text-slate-500">{title}</p>
      <p className="text-sm mt-1">This section is under development.</p>
    </div>
  );

  /* ════════════════════════════════════
     TAB CONTENT ROUTER
  ════════════════════════════════════ */
  const renderContent = () => {
    switch (activeTab) {
      case "overview": return data ? renderOverview() : <div className="p-6 text-slate-400 text-center">Loading...</div>;

      case "users-all": return renderUsersTable("All Users Registration");
      case "users-lbbp": return renderUsersTable("LBBP Active Users");
      case "users-puap": return renderUsersTable("PU & AP Active Members");

      case "kyc": return renderKYC();
      case "profile-update-requests": return <AdminProfileUpdateRequests onChanged={refreshSidebarCounts} />;
      case "mobile-store-applications": return renderMobileStoreApplications();
      case "new-withdrawals": return renderNewWithdrawals();
      case "withdrawals": return renderWithdrawalsHistory();
      case "fund-transfer": return <div className="p-4 sm:p-6"><MemberTransfer isAdminPanel /></div>;
      case "shares": return renderShares();
      case "share-purchases": return renderSharePurchases();
      case "royalty": return renderRoyalty();
      case "royalty-eligibility": return renderRoyaltyEligibility();
      case "new-payments": return renderNewPayments();
      case "payments": return renderAllPayments();
      case "transactions": return renderTransactions();
      case "products": return renderProducts();
      case "banners": return renderBanners();
      case "categories": return renderCategories();
      case "product-purchases": return renderProductPurchases();
      case "contact-list":
      case "user-queries":
      case "support-requests": return renderContact();

      /* PU&AP payouts */
      case "puap-direct": return renderPuapPayout("Direct Payout", puapDirectPayouts, puapDirectLoading, puapDirectSearch, setPuapDirectSearch, "direct_bonus");
      case "puap-matrix": return renderPuapPayout("Matrix Payout", puapMatrixPayouts, puapMatrixLoading, "", null, "matrix_income");
      case "puap-generation": return renderPuapPayout("Generation Payout", puapGenerationPayouts, puapGenerationLoading, "", null, "generation_income");
      case "puap-active-member": return renderPuapPayout("Active Member Payout", puapActiveMemberPayouts, puapActiveMemberLoading, "", null, "active_member_reward");
      case "puap-reward-tour": return renderPuapPayout("Reward Tour Bonus Payout", puapRewardTourPayouts, puapRewardTourLoading, "", null, "reward_tour_bonus");
      case "puap-car-bonus": return renderPuapPayout("Car Bonus Payout", puapCarBonusPayouts, puapCarBonusLoading, "", null, "car_bonus");
      case "puap-royalty": return renderPuapPayout("Royalty Payout", puapRoyaltyPayouts, puapRoyaltyLoading, "", null, "royalty_bonus");
      case "puap-transactions": return renderPuapPayout("PU & AP Transaction History", adminTransactions.filter(t => ["direct_bonus", "matrix_income", "generation_income", "product_income", "active_member_reward", "reward_tour_bonus", "car_bonus", "royalty_bonus", "weekly_payout", "wallet_puap", "puap_wallet", "puap_transfer", "puap_withdrawal", "puap_deposit"].includes(t.type) || t.type?.toLowerCase().includes("puap") || t.toWallet === "puap" || t.fromWallet === "puap"), adminTransactionsLoading, adminTransactionSearch, setAdminTransactionSearch, "all");

      default: return <div className="p-6 text-slate-400">Select a section</div>;
    }
  };

  /* ─── KYC ─── */
  const renderKYC = () => (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="text-lg font-bold text-slate-800">KYC Management</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={kycFilter} onChange={e => setKycFilter(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
            <option value="all">All</option><option value="pending">Pending</option><option value="verified">Verified</option><option value="rejected">Rejected</option>
          </select>
          <button onClick={fetchKYC} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[{ label: "Total", val: kycStats.total, color: "border-blue-600" }, { label: "Pending", val: kycStats.pending, color: "border-yellow-600" }, { label: "Verified", val: kycStats.verified, color: "border-green-600" }, { label: "Rejected", val: kycStats.rejected, color: "border-red-600" }].map(s => (
          <div key={s.label} className={`bg-white p-3 rounded-lg border-l-4 ${s.color}`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className="text-xl font-bold text-slate-800">{s.val}</p>
          </div>
        ))}
      </div>
      {!selectedKyc ? (
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          {kycFilter === "pending" && usersWithKyc.length > 0 && (
            <div className="p-3 border-b bg-slate-50 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selectAllKYC} onChange={e => { setSelectAllKYC(e.target.checked); setSelectedKYCUsers(e.target.checked ? usersWithKyc.map(u => u._id) : []); }} /> Select All
              </label>
              {selectedKYCUsers.length > 0 && <>
                <button onClick={handleApproveAllKYC} className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 flex items-center gap-1"><FiCheck size={14} /> Approve ({selectedKYCUsers.length})</button>
                <button onClick={handleRejectAllKYC} className="bg-red-600 text-white px-3 py-1.5 rounded text-sm hover:bg-red-700 flex items-center gap-1"><FiX size={14} /> Reject ({selectedKYCUsers.length})</button>
              </>}
            </div>
          )}
          <table className="w-full text-xs sm:text-sm min-w-[600px]">
            <thead className="bg-slate-100"><tr>
              {kycFilter === "pending" && <th className="px-4 py-3 text-left w-8"></th>}
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">PAN</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr></thead>
            <tbody>
              {usersWithKyc.length > 0 ? usersWithKyc.map(user => (
                <tr key={user._id} className="border-b hover:bg-slate-50">
                  {kycFilter === "pending" && <td className="px-4 py-3"><input type="checkbox" checked={selectedKYCUsers.includes(user._id)} onChange={e => setSelectedKYCUsers(e.target.checked ? [...selectedKYCUsers, user._id] : selectedKYCUsers.filter(id => id !== user._id))} /></td>}
                  <td className="px-4 py-3 font-semibold">{user.name} {user.uniqueId && <span className="font-normal text-slate-500">({user.uniqueId})</span>}</td>
                  <td className="px-4 py-3">{user.email}</td>
                  <td className="px-4 py-3 font-mono">{user.kycDocuments?.pan || "N/A"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${user.kycStatus === "verified" ? "bg-green-100 text-green-800" : user.kycStatus === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>{user.kycStatus?.toUpperCase()}</span></td>
                  <td className="px-4 py-3 text-slate-500">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}</td>
                  <td className="px-4 py-3"><button onClick={() => setSelectedKyc(user)} className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"><FiEye className="inline mr-1" />View</button></td>
                </tr>
              )) : <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No KYC requests found</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-slate-50 p-4 sm:p-6 rounded-lg">
          <div className="flex items-center justify-between mb-5">
            <h4 className="text-base font-bold text-slate-800">{selectedKyc.name} – KYC Details</h4>
            <button onClick={() => setSelectedKyc(null)} className="bg-slate-600 text-white px-3 py-1.5 rounded text-sm hover:bg-slate-700 flex items-center gap-1"><FiX size={14} /> Close</button>
          </div>
          
          {/* Personal Information */}
          <div className="bg-white p-4 rounded-lg border-l-4 border-blue-600 mb-4">
            <h5 className="font-semibold text-slate-800 mb-3 text-sm">Personal Information</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Email</p>
                <p className="font-semibold text-slate-800">{selectedKyc.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Date of Birth</p>
                <p className="font-semibold text-slate-800">{selectedKyc.dateOfBirth ? new Date(selectedKyc.dateOfBirth).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Mobile</p>
                <p className="font-semibold text-slate-800">{selectedKyc.mobile || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white p-4 rounded-lg border-l-4 border-green-600 mb-4">
            <h5 className="font-semibold text-slate-800 mb-3 text-sm">Location Information</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">State</p>
                <p className="font-semibold text-slate-800">{selectedKyc.state || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">District</p>
                <p className="font-semibold text-slate-800">{selectedKyc.district || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Tahsil</p>
                <p className="font-semibold text-slate-800">{selectedKyc.tahsil || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Post Office</p>
                <p className="font-semibold text-slate-800">{selectedKyc.postOffice || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Pin Code</p>
                <p className="font-semibold text-slate-800">{selectedKyc.pinCode || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Full Address</p>
                <p className="font-semibold text-slate-800">{selectedKyc.address || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Nominee Details */}
          <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-600 mb-4">
            <h5 className="font-semibold text-slate-800 mb-3 text-sm">Nominee Details</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Nominee Name</p>
                <p className="font-semibold text-slate-800">{selectedKyc.nomineeName || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Relation</p>
                <p className="font-semibold text-slate-800">{selectedKyc.nomineeRelation || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Nominee Phone</p>
                <p className="font-semibold text-slate-800">{selectedKyc.nomineePhone || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Profile Picture */}
          {selectedKyc.profilePic && (
            <div className="bg-white p-4 rounded-lg border-l-4 border-indigo-600 mb-4">
              <h5 className="font-semibold text-slate-800 mb-3 text-sm">Profile Picture</h5>
              <img 
                src={selectedKyc.profilePic} 
                alt="Profile" 
                className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                onClick={() => handleOpenImageModal(selectedKyc.profilePic, "Profile")}
              />
            </div>
          )}

          {/* Docs grid with images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-5">
            {/* PAN Card */}
            <div className="p-4 rounded border-l-4 border-blue-600 bg-blue-50">
              <p className="text-xs text-slate-500 mb-1">PAN Number</p>
              <p className="font-bold font-mono text-slate-800 mb-2">{selectedKyc.kycDocuments?.pan || "N/A"}</p>
              {selectedKyc.kycDocuments?.panFile && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">PAN Document</p>
                  <img 
                    src={selectedKyc.kycDocuments.panFile} 
                    alt="PAN Document" 
                    className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                    onClick={() => handleOpenImageModal(selectedKyc.kycDocuments.panFile, "PAN Document")}
                  />
                </div>
              )}
            </div>

            {/* Aadhaar Card */}
            <div className="p-4 rounded border-l-4 border-green-600 bg-green-50">
              <p className="text-xs text-slate-500 mb-1">Aadhaar Number</p>
              <p className="font-bold font-mono text-slate-800 mb-2">{selectedKyc.kycDocuments?.aadhaar || "N/A"}</p>
              
              {/* Aadhaar Front */}
              {(selectedKyc.kycDocuments?.aadhaarFront || selectedKyc.kycDocuments?.aadhaarFile) && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Aadhaar Front</p>
                  <img 
                    src={selectedKyc.kycDocuments?.aadhaarFront || selectedKyc.kycDocuments?.aadhaarFile} 
                    alt="Aadhaar Front" 
                    className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                    onClick={() => handleOpenImageModal(selectedKyc.kycDocuments?.aadhaarFront || selectedKyc.kycDocuments?.aadhaarFile, "Aadhaar Front")}
                  />
                </div>
              )}
              
              {/* Aadhaar Back */}
              {selectedKyc.kycDocuments?.aadhaarBack && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Aadhaar Back</p>
                  <img 
                    src={selectedKyc.kycDocuments?.aadhaarBack} 
                    alt="Aadhaar Back" 
                    className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                    onClick={() => handleOpenImageModal(selectedKyc.kycDocuments?.aadhaarBack, "Aadhaar Back")}
                  />
                </div>
              )}
            </div>

            {/* Bank Details */}
            <div className="p-4 rounded border-l-4 border-purple-600 bg-purple-50">
              <p className="text-xs text-slate-500 mb-1">Bank Name</p>
              <p className="font-bold text-slate-800 mb-2">{selectedKyc.kycDocuments?.bankName || "N/A"}</p>
              {selectedKyc.kycDocuments?.bankFile && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Bank Passbook</p>
                  <img 
                    src={selectedKyc.kycDocuments?.bankFile} 
                    alt="Bank Passbook" 
                    className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                    onClick={() => handleOpenImageModal(selectedKyc.kycDocuments?.bankFile, "Bank Passbook")}
                  />
                </div>
              )}
            </div>
            <div className="p-4 rounded border-l-4 border-orange-600 bg-orange-50">
              <p className="text-xs text-slate-500 mb-1">Account Number</p>
              <p className="font-bold font-mono text-slate-800">{selectedKyc.kycDocuments?.bankAccount || "N/A"}</p>
            </div>
            <div className="p-4 rounded border-l-4 border-pink-600 bg-pink-50">
              <p className="text-xs text-slate-500 mb-1">IFSC Code</p>
              <p className="font-bold font-mono text-slate-800">{selectedKyc.kycDocuments?.ifsc || "N/A"}</p>
            </div>
          </div>
          {selectedKyc.kycStatus === "pending" && (
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button onClick={() => handleApproveKYC(selectedKyc._id)} className="bg-green-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"><FiCheck /> Approve KYC</button>
            </div>
          )}
          <div className="bg-white p-4 rounded border-l-4 border-red-600">
            <p className="font-semibold text-slate-800 mb-2">Reject KYC</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason..." className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg mb-2 text-sm" rows="2" />
            <button onClick={() => handleRejectKYC(selectedKyc._id)} className="bg-red-600 text-white px-5 py-2 rounded font-semibold hover:bg-red-700 flex items-center gap-2"><FiX /> Reject</button>
          </div>
        </div>
      )}
    </div>
  );

  /* ─── Mobile Store Applications ─── */
  const renderMobileStoreApplications = () => (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="text-lg font-bold text-slate-800">Mobile Store Applications</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={mobileStoreFilter} onChange={e => setMobileStoreFilter(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={fetchMobileStoreApplications} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[{ label: "Total", val: mobileStoreApps.length, color: "border-blue-600" }, { label: "Pending", val: mobileStoreApps.filter(a => a.status === "pending").length, color: "border-yellow-600" }, { label: "Approved", val: mobileStoreApps.filter(a => a.status === "approved").length, color: "border-green-600" }, { label: "Rejected", val: mobileStoreApps.filter(a => a.status === "rejected").length, color: "border-red-600" }].map(s => (
          <div key={s.label} className={`bg-white p-3 rounded-lg border-l-4 ${s.color}`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className="text-xl font-bold text-slate-800">{s.val}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[800px]">
          <thead className="bg-slate-100"><tr>
            <th className="px-4 py-3 text-left">Applicant</th>
            <th className="px-4 py-3 text-left">Shop Name</th>
            <th className="px-4 py-3 text-left">Shop Address</th>
            <th className="px-4 py-3 text-left">GST Number</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Applied Date</th>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr></thead>
          <tbody>
            {mobileStoreApps.filter(app => mobileStoreFilter === "all" || app.status === mobileStoreFilter).length > 0 ? (
              mobileStoreApps.filter(app => mobileStoreFilter === "all" || app.status === mobileStoreFilter).map(app => (
                <tr key={app._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-semibold">{app.userId?.name || "—"}</p>
                    <p className="text-xs text-slate-500">{app.userId?.uniqueId}</p>
                    <p className="text-xs text-slate-500">{app.userId?.mobile}</p>
                  </td>
                  <td className="px-4 py-3">{app.shopName || "—"}</td>
                  <td className="px-4 py-3 text-xs max-w-xs truncate">{app.shopAddress || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{app.gstNumber || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${app.status === "approved" ? "bg-green-100 text-green-800" : app.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {app.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : "N/A"}</td>
                  <td className="px-4 py-3">
                    {app.status === "pending" ? (
                      <div className="flex gap-1">
                        <button onClick={() => handleApproveMobileStore(app._id)} className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 flex items-center gap-1"><FiCheck size={12} /> Approve</button>
                        <button onClick={() => { setSelectedMobileStoreApp(app); setShowRejectModal(true); setRejectionReason(""); }} className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 flex items-center gap-1"><FiX size={12} /> Reject</button>
                      </div>
                    ) : app.status === "approved" ? (
                      <span className="text-xs text-green-600 font-semibold">ID: {app.mobileStoreId}</span>
                    ) : (
                      <span className="text-xs text-slate-400">{app.rejectionReason || "Rejected"}</span>
                    )}
                  </td>
                </tr>
              ))
            ) : <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No applications found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 mt-16 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
            <button onClick={() => { setShowRejectModal(false); setRejectionReason(""); setSelectedMobileStoreApp(null); }} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
              <FiX size={20} />
            </button>
            <h3 className="text-lg font-bold text-slate-800 mb-4">Reject Application</h3>
            <p className="text-sm text-slate-600 mb-4">Please provide a reason for rejecting this Mobile Store application.</p>
            <textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg mb-4 text-sm"
              rows="3"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(""); setSelectedMobileStoreApp(null); }} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleRejectMobileStore} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ─── Withdrawals ─── */
  const WithdrawalRow = ({ w, showActions }) => (
    <tr className="border-b hover:bg-slate-50">
      <td className="px-4 py-3"><p className="font-semibold">{w.userId?.name || "—"}</p><p className="text-xs text-slate-500">{w.userId?.uniqueId}</p><p className="text-xs text-slate-500">{w.userId?.mobile}</p></td>
      <td className="px-4 py-3 font-bold">₹{w.amount}</td>
      <td className="px-4 py-3 text-xs">{w.bankName ? <><p>Bank: {w.bankName}</p>{w.accountNumber && <p>A/C: {w.accountNumber}</p>}{w.ifsc && <p>IFSC: {w.ifsc}</p>}</> : w.upiId ? <p>UPI: {w.upiId}</p> : "—"}</td>
      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${w.status === "approved" ? "bg-green-100 text-green-800" : w.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>{w.status}</span></td>
      <td className="px-4 py-3 text-xs text-slate-500">{new Date(w.createdAt).toLocaleDateString()}</td>
      {showActions && <td className="px-4 py-3">
        {w.status === "pending" ? (
          <div className="flex gap-1">
            <button onClick={() => handleApproveWithdrawal(w._id)} className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"><FiCheck /></button>
            <button onClick={() => handleRejectWithdrawal(w._id)} className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"><FiX /></button>
          </div>
        ) : <span className="text-xs text-slate-400 capitalize">{w.status}</span>}
      </td>}
    </tr>
  );

  const renderNewWithdrawals = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">Pending Withdrawals</h3>
        <button onClick={fetchWithdrawalsPendings} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Bank/UPI</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
          <tbody>
            {withdrawals.filter(w => w.status === "pending").length > 0
              ? withdrawals.filter(w => w.status === "pending").map(w => <WithdrawalRow key={w._id} w={w} showActions />)
              : <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No pending withdrawals</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderWithdrawalsHistory = () => (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="text-lg font-bold text-slate-800">Withdrawal History</h3>
        <div className="flex items-center gap-2">
          <select value={withdrawalFilter} onChange={e => setWithdrawalFilter(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
            <option value="all">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
          <button onClick={fetchWithdrawals} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Bank/UPI</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
          <tbody>
            {withdrawals.filter(w => withdrawalFilter === "all" || w.status === withdrawalFilter).length > 0
              ? withdrawals.filter(w => withdrawalFilter === "all" || w.status === withdrawalFilter).map(w => <WithdrawalRow key={w._id} w={w} showActions />)
              : <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No withdrawals found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ─── Shares ─── */
  const renderShares = () => {
    return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">GCP Loan Plans</h3>
        <button onClick={() => { showCreateForm ? resetShareForm() : setShowCreateForm(true) }} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
          {showCreateForm ? "Close" : "+ Create Plan"}
        </button>
      </div>
      {showCreateForm && (
        <form onSubmit={handleCreateShare} className="bg-blue-50 p-4 sm:p-6 rounded-lg mb-6">
          <h4 className="font-bold text-slate-800 mb-4">{editingShareId ? "Edit Plan" : "New Plan"}</h4>
          {/* Add floating label styles */}
          <style>{`
            .floating-label {
              position: relative;
              margin-bottom: 1rem;
            }
            .floating-input {
              width: 100%;
              padding: 1rem 0.75rem 0.5rem;
              border: 2px solid #cbd5e1;
              border-radius: 0.5rem;
              font-size: 0.875rem;
              outline: none;
              background: white;
              transition: border-color 0.2s;
            }
            .floating-input:focus {
              border-color: #3b82f6;
            }
            .floating-input:focus + label,
            .floating-input:not(:placeholder-shown) + label {
              top: 0.25rem;
              left: 0.75rem;
              font-size: 0.85rem;
              color: #3b82f6;
              background: white;
              padding: 0 0.25rem;
            }
            .floating-label label {
              position: absolute;
              top: 50%;
              left: 0.75rem;
              transform: translateY(-50%);
              color: #64748b;
              font-size: 0.875rem;
              pointer-events: none;
              transition: all 0.2s ease;
            }
          `}</style>
          {(() => {
            const inv = Number(newShare.price) || 0, rate = Number(newShare.roiPercentage) || 0, wks = Number(newShare.cycleWeeks) || 0;
            const roi = (inv * rate) / 100, total = inv + roi, weekly = wks > 0 ? total / wks : 0;
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="floating-label">
                    <input 
                      type="text" 
                      value={newShare.name} 
                      onChange={e => setNewShare({ ...newShare, name: e.target.value })} 
                      placeholder=" " 
                      className="floating-input" 
                      required 
                    />
                    <label>Plan Name</label>
                  </div>
                  <div className="floating-label">
                    <input 
                      type="number" 
                      value={newShare.price} 
                      onChange={e => setNewShare({ ...newShare, price: e.target.value })} 
                      placeholder=" " 
                      className="floating-input" 
                      required 
                    />
                    <label>Investment Amount (₹)</label>
                  </div>
                  <div className="floating-label">
                    <input 
                      type="number" 
                      value={newShare.cycleWeeks} 
                      onChange={e => setNewShare({ ...newShare, cycleWeeks: e.target.value })} 
                      placeholder=" " 
                      className="floating-input" 
                      required 
                    />
                    <label>Cycle Weeks</label>
                  </div>
                  <div className="floating-label">
                    <input 
                      type="number" 
                      value={newShare.roiPercentage} 
                      onChange={e => setNewShare({ ...newShare, roiPercentage: e.target.value })} 
                      placeholder=" " 
                      className="floating-input" 
                      required 
                    />
                    <label>Interest Rate (%)</label>
                  </div>
                  <div className="floating-label">
                    <input 
                      type="number" 
                      value={newShare.totalShares} 
                      onChange={e => setNewShare({ ...newShare, totalShares: e.target.value })} 
                      placeholder=" " 
                      className="floating-input" 
                      min="0" 
                    />
                    <label>Total Shares (0 = unlimited)</label>
                  </div>
                  <div>
                    <input type="file" accept="image/*" onChange={e => setPlanImageFile(e.target.files[0] || null)} className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg text-sm bg-white" />
                    {planImageFile && <p className="text-xs text-slate-500 mt-1">{planImageFile.name}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center"><p className="text-xs text-slate-500">EMI Amount</p><p className="font-bold text-blue-700">₹{roi.toFixed(2)}</p></div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center"><p className="text-xs text-slate-500">Total Return</p><p className="font-bold text-green-700">₹{total.toFixed(2)}</p></div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center"><p className="text-xs text-slate-500">Weekly</p><p className="font-bold text-purple-700">{wks > 0 ? `₹${weekly.toFixed(2)}` : "—"}</p></div>
                </div>
                <div className="flex gap-3">
                  <button type="submit" disabled={planImageUploading} className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700 disabled:opacity-60">{planImageUploading ? "Uploading..." : editingShareId ? "Update" : "Create"}</button>
                  <button type="button" onClick={resetShareForm} className="bg-slate-600 text-white px-6 py-2 rounded font-semibold hover:bg-slate-700">Cancel</button>
                </div>
              </>
            );
          })()}
        </form>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {shares.length > 0 ? shares.map(share => {
          const purchaseCount = Number(share.purchasedShares || 0);
          const totalShares = Number(share.totalShares || 0);
          const availableShares = totalShares > 0 ? Math.max(0, totalShares - purchaseCount) : Infinity;
          
          return (
          <div key={share._id} className="bg-white rounded-xl shadow border border-slate-200 p-4 hover:shadow-md transition">
            <div className="flex items-center gap-3 mb-3">
              {share.featuredImage ? <img src={share.featuredImage} alt={share.name} className="w-12 h-12 rounded-lg object-cover border" /> : <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">No Img</div>}
              <div className="flex-1">
                <p className="font-bold text-slate-800">{share.name}</p>
                <p className="text-sm text-slate-500">₹{share.price}</p>
                {purchaseCount > 0 && <p className="text-xs text-blue-600 font-semibold">Purchased {purchaseCount} time{purchaseCount !== 1 ? 's' : ''}</p>}
                {totalShares > 0 && (
                  <p className="text-xs text-slate-600 font-semibold mt-1">
                    {availableShares === 0 ? (
                      <span className="text-red-600">Sold Out ({totalShares} total)</span>
                    ) : (
                      `Available: ${availableShares} / ${totalShares} total`
                    )}
                  </p>
                )}
              </div>
            </div>
            
            {totalShares > 0 && (
              <div className="mb-3">
                <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${availableShares === 0 ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
                    style={{ width: `${Math.min(100, (purchaseCount / totalShares) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <p><span className="text-slate-500">Cycle:</span> {share.cycleWeeks ?? Math.ceil((share.cycleDays || 0) / 7)} wks</p>
              <p><span className="text-slate-500">Interest:</span> {share.roiPercentage || 0}%</p>
              <p><span className="text-slate-500">EMI:</span> <span className="font-semibold text-green-600">₹{Number(share.roiAmount).toFixed(2)}</span></p>
              <p><span className="text-slate-500">Total:</span> <span className="font-semibold text-green-700">₹{Number(share.totalReturn ?? (Number(share.price) + Number(share.roiAmount))).toFixed(2)}</span></p>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${share.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{share.isActive ? "Active" : "Inactive"}</span>
              <button onClick={() => handleTogglePurchase(share)} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${share.isPurchaseEnabled !== false ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{share.isPurchaseEnabled !== false ? "🟢 Purchase On" : "🔴 Purchase Off"}</button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleToggleShareStatus(share)} className={`flex-1 py-1.5 rounded text-xs font-semibold text-white ${share.isActive ? "bg-slate-600 hover:bg-slate-700" : "bg-green-600 hover:bg-green-700"}`}>{share.isActive ? "Deactivate" : "Activate"}</button>
              <button onClick={() => handleEditShare(share)} className="flex-1 bg-yellow-500 text-white py-1.5 rounded text-xs font-semibold hover:bg-yellow-600">Edit</button>
              <button onClick={() => handleDeleteShare(share._id)} className="flex-1 bg-red-500 text-white py-1.5 rounded text-xs font-semibold hover:bg-red-600">Delete</button>
            </div>
          </div>
        )}) : <div className="col-span-full text-center text-slate-500 py-12">No share plans yet</div>}
      </div>
    </div>
  )};

  /* ─── Share Purchases ─── */
  const renderSharePurchases = () => {
    const filtered = getFilteredSharePurchases();
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-800">GCP Loan History</h3>
          <button onClick={fetchSharePurchases} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <input type="text" placeholder="Search..." value={sharePurchaseSearch} onChange={e => setSharePurchaseSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <select value={sharePurchaseStatusFilter} onChange={e => setSharePurchaseStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
            <option value="all">All Status</option><option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
          <select value={sharePurchaseDateFilter} onChange={e => { setSharePurchaseDateFilter(e.target.value); if (e.target.value !== "custom") { setSharePurchaseStartDate(""); setSharePurchaseEndDate(""); } }} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
            <option value="all">All Dates</option><option value="today">Today</option><option value="thisMonth">This Month</option><option value="thisYear">This Year</option><option value="custom">Custom</option>
          </select>
        </div>
        {sharePurchaseDateFilter === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label><input type="date" value={sharePurchaseStartDate} onChange={e => setSharePurchaseStartDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">End Date</label><input type="date" value={sharePurchaseEndDate} onChange={e => setSharePurchaseEndDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[{ label: "Showing", val: filtered.length }, { label: "Total Invested", val: `₹${filtered.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}` }, { label: "Active", val: filtered.filter(p => p.status === "active").length }, { label: "Completed", val: filtered.filter(p => p.status === "completed").length }].map(c => (
            <div key={c.label} className="bg-blue-50 rounded-xl p-3 border border-blue-100"><p className="text-xs text-slate-500">{c.label}</p><p className="text-lg font-bold text-blue-700">{c.val}</p></div>
          ))}
        </div>
        {sharePurchasesLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : filtered.length === 0 ? <div className="py-12 text-center text-slate-400">No purchases found.</div> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs sm:text-sm min-w-[700px]">
              <thead className="bg-slate-100"><tr>
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-left">User</th>
                <th className="px-3 py-3 text-left">Plan</th>
                <th className="px-3 py-3 text-left">Amount</th>
                <th className="px-3 py-3 text-left">Payout</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-left">Dates</th>
              </tr></thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p._id} className="border-b hover:bg-slate-50 align-top">
                    <td className="px-3 py-3 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-3 text-slate-500">{new Date(p.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-3 py-3"><p className="font-semibold">{p.userId?.name || "—"}</p><p className="text-slate-500">{p.userId?.uniqueId}</p><p className="text-slate-500">{p.userId?.mobile}</p></td>
                    <td className="px-3 py-3"><p className="font-medium">{p.planId?.name || "—"}</p><p className="text-slate-500">₹{p.planId?.price} | {p.planId?.cycleWeeks ?? Math.ceil((p.planId?.cycleDays || 0) / 7)}wks | {p.planId?.roiPercentage}%</p></td>
                    <td className="px-3 py-3"><p>Loan: <span className="font-semibold">₹{Number(p.amount || 0).toLocaleString()}</span></p><p>EMI: <span className="font-semibold text-blue-700">₹{Number(p.roiAmount || 0).toLocaleString()}</span></p><p>Total: <span className="font-semibold text-green-700">₹{Number(p.totalReturn ?? ((p.amount || 0) + (p.roiAmount || 0))).toLocaleString()}</span></p></td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-1 ${p.isAutoPayoutEnabled !== false ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{p.isAutoPayoutEnabled !== false ? "Auto" : "Manual"}</span>
                      <div className="flex flex-col gap-1 mt-1">
                        <button onClick={() => handleTogglePayoutMode(p)} className={`px-2 py-0.5 rounded text-xs text-white ${p.isAutoPayoutEnabled !== false ? "bg-orange-600 hover:bg-orange-700" : "bg-blue-600 hover:bg-blue-700"}`}>{p.isAutoPayoutEnabled !== false ? "→ Manual" : "→ Auto"}</button>
                        <button onClick={() => handleManualWeeklyCredit(p)} disabled={p.status !== "active"} className="px-2 py-0.5 rounded text-xs text-white bg-green-600 hover:bg-green-700 disabled:bg-slate-300">Credit Now</button>
                      </div>
                    </td>
                    <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === "active" ? "bg-green-100 text-green-700" : p.status === "completed" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{p.status}</span></td>
                    <td className="px-3 py-3 text-slate-500"><p>Start: {p.cycleStart ? new Date(p.cycleStart).toLocaleDateString("en-IN") : "—"}</p><p>End: {p.cycleEnd ? new Date(p.cycleEnd).toLocaleDateString("en-IN") : "—"}</p></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /* ─── Royalty History ─── */
  const renderRoyalty = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">Royalty History</h3>
        <button onClick={() => fetchRoyaltyRecords(1)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {[["Total", `₹${royaltySummary.totalAmount.toLocaleString()}`, "from-purple-500 to-purple-600"], ["Records", royaltySummary.totalRecords, "from-blue-500 to-blue-600"], ["Average", `₹${Math.round(royaltySummary.averageAmount).toLocaleString()}`, "from-green-500 to-green-600"], ["Highest", `₹${royaltySummary.maxAmount.toLocaleString()}`, "from-yellow-500 to-yellow-600"], ["Lowest", `₹${royaltySummary.minAmount.toLocaleString()}`, "from-red-500 to-red-600"]].map(([lbl, val, gr]) => (
          <div key={lbl} className={`bg-gradient-to-r ${gr} text-white rounded-lg p-3`}><p className="text-xs opacity-90">{lbl}</p><p className="text-lg font-bold">{val}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="relative"><FiSearch className="absolute left-3 top-3 text-slate-400" /><input type="text" placeholder="Search by user..." value={royaltySearch} onChange={e => setRoyaltySearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" /></div>
        <select value={royaltyMonthFilter} onChange={e => setRoyaltyMonthFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
          <option value="">All Months</option>{[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{new Date(2024, i).toLocaleDateString("en-US", { month: "long" })}</option>)}
        </select>
        <select value={royaltyYearFilter} onChange={e => setRoyaltyYearFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
          <option value="">All Years</option>{[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={royaltyStatusFilter} onChange={e => setRoyaltyStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
          <option value="all">All Status</option><option value="calculated">Calculated</option><option value="credited">Credited</option><option value="pending">Pending</option>
        </select>
      </div>
      {royaltyLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : royaltyRecords.length === 0 ? <div className="py-12 text-center text-slate-400">No records found</div> : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs sm:text-sm min-w-[700px]">
            <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Period</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Tier</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
            <tbody>
              {royaltyRecords.map(r => (
                <tr key={r._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{new Date(r.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="px-4 py-3"><p className="font-semibold">{r.userId?.name || "Unknown"}</p><p className="text-slate-500">{r.userId?.uniqueId}</p></td>
                  <td className="px-4 py-3"><p className="font-semibold">{new Date(r.year, r.month - 1).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</p></td>
                  <td className="px-4 py-3"><p className="font-bold text-purple-600">₹{Number(r.amount || 0).toLocaleString()}</p><p className="text-slate-500">{r.royaltyPercentage}%</p></td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.royaltyPercentage === 5 ? "bg-yellow-100 text-yellow-700" : r.royaltyPercentage === 3 ? "bg-gray-100 text-gray-700" : "bg-orange-100 text-orange-700"}`}>{r.royaltyPercentage === 5 ? "Gold" : r.royaltyPercentage === 3 ? "Silver" : "Bronze"}</span></td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === "credited" ? "bg-green-100 text-green-700" : r.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{r.status || "calculated"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between p-3 border-t bg-slate-50">
            <p className="text-xs text-slate-500">Page {royaltyPagination.current} of {royaltyPagination.total}</p>
            <div className="flex gap-2">
              {royaltyPagination.current > 1 && <button onClick={() => fetchRoyaltyRecords(royaltyPagination.current - 1)} className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700">Prev</button>}
              {royaltyPagination.current < royaltyPagination.total && <button onClick={() => fetchRoyaltyRecords(royaltyPagination.current + 1)} className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700">Next</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ─── Royalty Eligibility ─── */
  const renderRoyaltyEligibility = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="text-lg font-bold text-slate-800">Royalty Eligibility</h3><p className="text-xs text-slate-500">{new Date(currentYear, currentMonth - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p></div>
        <button onClick={() => fetchRoyaltyEligibility()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2"><FiCalendar className="text-slate-500" />
          <select value={currentMonth} onChange={e => setCurrentMonth(parseInt(e.target.value))} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
            {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{new Date(2024, i).toLocaleDateString("en-US", { month: "long" })}</option>)}
          </select>
          <select value={currentYear} onChange={e => setCurrentYear(parseInt(e.target.value))} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[{ lbl: "Total Eligible", val: eligibilitySummary.totalEligible, cls: "from-purple-500 to-purple-600" }, { lbl: "Total Users", val: eligibilitySummary.totalUsers, cls: "from-blue-500 to-blue-600" }, { lbl: "Royalty Amount", val: `₹${Number(eligibilitySummary.totalRoyaltyAmount || 0).toLocaleString()}`, cls: "from-green-500 to-green-600" }, { lbl: "Eligibility Rate", val: `${eligibilitySummary.totalUsers > 0 ? Math.round((eligibilitySummary.totalEligible / eligibilitySummary.totalUsers) * 100) : 0}%`, cls: "from-yellow-500 to-yellow-600" }].map(c => (
          <div key={c.lbl} className={`bg-gradient-to-r ${c.cls} text-white rounded-lg p-3`}><p className="text-xs opacity-90">{c.lbl}</p><p className="text-xl font-bold">{c.val}</p></div>
        ))}
      </div>
      {eligibilityLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : eligibleUsers.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4"><p className="text-sm font-semibold text-amber-800">No eligible users for this period.</p></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs sm:text-sm min-w-[700px]">
            <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Eligible</th><th className="px-4 py-3 text-left">Tier</th><th className="px-4 py-3 text-left">Royalty</th><th className="px-4 py-3 text-left">Personal</th><th className="px-4 py-3 text-left">Team</th><th className="px-4 py-3 text-left">Members</th></tr></thead>
            <tbody>
              {eligibleUsers.filter(u => {
                const q = eligibilitySearch.toLowerCase();
                return !q || u.name?.toLowerCase().includes(q) || u.uniqueId?.toLowerCase().includes(q) || u.mobile?.includes(q);
              }).map(u => (
                <tr key={u._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3"><p className="font-semibold">{u.name}</p><p className="text-slate-500">{u.uniqueId}</p></td>
                  <td className="px-4 py-3">{u.isEligible ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold">✓ Eligible</span> : <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold">✗ Not Eligible</span>}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.tier?.tierName === "gold" ? "bg-yellow-100 text-yellow-700" : u.tier?.tierName === "silver" ? "bg-gray-100 text-gray-700" : "bg-orange-100 text-orange-700"}`}>{u.tier?.tierName || "—"}</span></td>
                  <td className="px-4 py-3 font-bold text-purple-600">{u.isEligible ? `₹${Number(u.estimatedRoyalty || 0).toLocaleString()}` : "₹0"}</td>
                  <td className="px-4 py-3">₹{Number(u.personalInvestment || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">₹{Number(u.downlineInvestment || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><p>Direct: {u.activeDirectMembers || 0}</p><p>Total: {u.totalActiveMembers || 0}</p></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  /* ─── Payments ─── */
  const renderNewPayments = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">New Add Money Requests</h3>
        <button onClick={fetchPayments} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>
      {paymentsLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><p className="text-sm font-semibold text-yellow-800">Pending</p><p className="text-2xl font-bold text-yellow-600">{payments.filter(p => p.status === "pending").length}</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4"><p className="text-sm font-semibold text-blue-800">Total Amount</p><p className="text-2xl font-bold text-blue-600">₹{payments.filter(p => p.status === "pending").reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}</p></div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              {payments.filter(p => p.status === "pending").length > 0 && (
                <button onClick={() => setBulkApproveConfirm(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 w-full">Approve All</button>
              )}
            </div>
          </div>
          {payments.filter(p => p.status === "pending").length === 0 ? (
            <div className="py-12 text-center text-slate-400"><FiCheckCircle size={36} className="mx-auto mb-2 text-slate-300" /><p>No pending payments</p></div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs sm:text-sm min-w-[600px]">
                <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">UPI/Txn</th><th className="px-4 py-3 text-left">Screenshot</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
                <tbody>
                  {payments.filter(p => p.status === "pending").map(p => (
                    <tr key={p._id} className="border-b hover:bg-yellow-50">
                      <td className="px-4 py-3 text-slate-500">{new Date(p.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-3"><p className="font-semibold">{p.userId?.name || "—"}</p><p className="text-slate-500">{p.userId?.uniqueId}</p></td>
                      <td className="px-4 py-3 font-bold text-lg">₹{p.amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs"><p>{p.upiId}</p>{p.transactionId && <p>{p.transactionId}</p>}</td>
                      <td className="px-4 py-3"><button onClick={() => handleOpenImageModal(p.screenshotUrl, "Payment Screenshot")} className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700">View</button></td>
                      <td className="px-4 py-3">
                        {reviewingPaymentId === p._id ? (
                          <div className="space-y-1"><input type="text" placeholder="Note..." value={paymentNote} onChange={e => setPaymentNote(e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" />
                            <div className="flex gap-1">
                              <button disabled={paymentActionLoading === p._id} onClick={() => handleReviewPayment(p._id, "approved")} className={`bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 ${paymentActionLoading === p._id ? "opacity-50 cursor-not-allowed" : ""}`}><FiCheck /></button>
                              <button disabled={paymentActionLoading === p._id} onClick={() => handleReviewPayment(p._id, "rejected")} className={`bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 ${paymentActionLoading === p._id ? "opacity-50 cursor-not-allowed" : ""}`}><FiX /></button>
                              <button disabled={paymentActionLoading === p._id} onClick={() => { setReviewingPaymentId(null); setPaymentNote(""); }} className={`bg-slate-400 text-white px-2 py-1 rounded text-xs ${paymentActionLoading === p._id ? "opacity-50 cursor-not-allowed" : ""}`}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <button onClick={() => setReviewingPaymentId(p._id)} className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                            <button onClick={() => setReviewingPaymentId(p._id)} className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700">Review</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderAllPayments = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">Add Money History</h3>
        <button onClick={fetchPayments} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>
      {paymentsLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : payments.length === 0 ? <div className="py-12 text-center text-slate-400">No payment submissions yet.</div> : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs sm:text-sm min-w-[600px]">
            <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">UPI/Txn</th><th className="px-4 py-3 text-left">Screenshot</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Approved Date</th></tr></thead>
            <tbody>
              {payments.map(p => (
                <tr key={p._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{new Date(p.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-3"><p className="font-semibold">{p.userId?.name || "—"}</p><p className="text-slate-500">{p.userId?.uniqueId}</p><p className="text-slate-500">{p.userId?.mobile}</p></td>
                  <td className="px-4 py-3 font-bold">₹{p.amount?.toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs"><p>{p.upiId}</p>{p.transactionId && <p>{p.transactionId}</p>}</td>
                  <td className="px-4 py-3"><button onClick={() => handleOpenImageModal(p.screenshotUrl, "Payment Screenshot")} className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700">View</button></td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === "approved" ? "bg-green-100 text-green-700" : p.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{p.reviewedAt ? new Date(p.reviewedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  /* ─── Transactions ─── */
  const renderTransactions = () => {
    const q = adminTransactionSearch.trim().toLowerCase();
    // Filter out PU&AP transaction types from LBBP Transaction History
    const puapTransactionTypes = ["direct_bonus", "matrix_income", "generation_income", "product_income", "active_member_reward", "reward_tour_bonus", "car_bonus", "royalty_bonus", "weekly_payout", "wallet_puap", "puap_wallet", "puap_transfer", "puap_withdrawal", "puap_deposit"];
    const filtered = adminTransactions.filter(tx => {
      const isPuapTransaction = puapTransactionTypes.includes(tx.type) || tx.type?.toLowerCase().includes("puap") || tx.toWallet === "puap" || tx.fromWallet === "puap";
      const matchSearch = !q || tx.userId?.name?.toLowerCase().includes(q) || tx.userId?.uniqueId?.toLowerCase().includes(q) || tx.userId?.mobile?.includes(q) || tx.type?.toLowerCase().includes(q) || tx.description?.toLowerCase().includes(q);
      return !isPuapTransaction && matchSearch && (adminTransactionTypeFilter === "all" || tx.type === adminTransactionTypeFilter);
    });
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-800">LBBP Transaction History</h3>
          <button onClick={fetchAdminTransactions} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="relative sm:col-span-2"><FiSearch className="absolute left-3 top-3 text-slate-400" /><input type="text" placeholder="Search..." value={adminTransactionSearch} onChange={e => setAdminTransactionSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" /></div>
          <select value={adminTransactionTypeFilter} onChange={e => setAdminTransactionTypeFilter(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">
            <option value="all">All Types</option>
            {Array.from(new Set(adminTransactions.map(tx => tx.type).filter(Boolean))).filter(type => !puapTransactionTypes.includes(type)).map(type => <option key={type} value={type}>{type.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        {adminTransactionsLoading ? <div className="py-12 text-center text-slate-400">Loading...</div> : filtered.length === 0 ? <div className="py-12 text-center text-slate-400">No transactions found</div> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs sm:text-sm min-w-[600px]">
              <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Details</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
              <tbody>
                {filtered.map(tx => {
                  const tc = getTransactionTypeStyle(tx.type);
                  return (
                    <tr key={tx._id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500">{new Date(tx.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td className="px-4 py-3"><p className="font-semibold">{tx.userId?.name || "—"}</p><p className="text-slate-500">{tx.userId?.uniqueId}</p></td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tc.bg} ${tc.text}`}>{tx.type?.replace(/_/g, " ") || "—"}</span></td>
                      <td className="px-4 py-3 font-bold text-lg"><span className={tx.amount >= 0 ? "text-emerald-600" : "text-red-600"}>{tx.amount >= 0 ? "+" : ""}₹{Number(tx.amount || 0).toLocaleString("en-IN")}</span></td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs">{tx.description && <p className="text-xs">{tx.description}</p>}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${!tx.approvalStatus || tx.approvalStatus === "approved" ? "bg-emerald-100 text-emerald-700" : tx.approvalStatus === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{tx.approvalStatus ? tx.approvalStatus.toUpperCase() : "COMPLETED"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /* ─── PU&AP Payouts ─── */
  const renderPuapPayout = (title, payouts, loading, searchValue, setSearchValue, filterType) => {
    const q = searchValue?.trim().toLowerCase() || "";
    const filtered = payouts.filter(tx => {
      const matchSearch = !q || tx.userId?.name?.toLowerCase().includes(q) || tx.userId?.uniqueId?.toLowerCase().includes(q) || tx.userId?.mobile?.includes(q) || tx.description?.toLowerCase().includes(q);
      return matchSearch;
    });
    const totalAmount = filtered.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    return (
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          <div className="flex items-center gap-2">
            <div className="bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-lg">
              <p className="text-xs text-emerald-600 font-medium">Total Payout</p>
              <p className="text-lg font-bold text-emerald-700">₹{totalAmount.toLocaleString("en-IN")}</p>
            </div>
            <button onClick={() => {
              if (title === "Direct Payout") fetchPuapDirectPayouts();
              else if (title === "Matrix Payout") fetchPuapMatrixPayouts();
              else if (title === "Generation Payout") fetchPuapGenerationPayouts();
              else if (title === "Active Member Payout") fetchPuapActiveMemberPayouts();
              else if (title === "Reward Tour Bonus Payout") fetchPuapRewardTourPayouts();
              else if (title === "Car Bonus Payout") fetchPuapCarBonusPayouts();
              else if (title === "Royalty Payout") fetchPuapRoyaltyPayouts();
              else if (title === "PU & AP Transaction History") fetchAdminTransactions();
            }} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
          </div>
        </div>
        {searchValue !== undefined && (
          <div className="mb-5">
            <div className="relative"><FiSearch className="absolute left-3 top-3 text-slate-400" /><input type="text" placeholder="Search by name, ID, mobile..." value={searchValue} onChange={e => setSearchValue && setSearchValue(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" /></div>
          </div>
        )}
        {loading ? <div className="py-12 text-center text-slate-400">Loading...</div> : filtered.length === 0 ? <div className="py-12 text-center text-slate-400">No payouts found</div> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs sm:text-sm min-w-[600px]">
              <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Recipient</th><th className="px-4 py-3 text-left">Unique ID</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Description</th></tr></thead>
              <tbody>
                {filtered.map(tx => (
                  <tr key={tx._id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">{new Date(tx.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-3"><p className="font-semibold">{tx.userId?.name || "—"}</p><p className="text-slate-500">{tx.userId?.mobile || "—"}</p></td>
                    <td className="px-4 py-3 font-medium text-slate-700">{tx.userId?.uniqueId || "—"}</td>
                    <td className="px-4 py-3 font-bold text-lg text-emerald-600">+₹{Number(tx.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs text-xs">{tx.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /* ─── Products ─── */
  const renderProducts = () => {
    const selectedCategory = categories.find(c => c._id === newProduct.category);
    const subcategories = selectedCategory?.subcategories || [];
    
    return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">Products</h3>
        <div className="flex gap-2">
          <button onClick={fetchProducts} className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"><FiRefreshCw size={14} /></button>
          <button onClick={() => setShowProductForm(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"><FiPlus size={14} /> Add Product</button>
        </div>
      </div>
      {showProductForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-slate-800">{editingProductId ? "Edit Product" : "Add Product"}</h4>
            <button onClick={resetProductForm} className="text-slate-400 hover:text-slate-600"><FiX size={20} /></button>
          </div>
          <form onSubmit={handleProductSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Name *</label><input type="text" required value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">MRP *</label><input type="number" required min="0" value={newProduct.mrp} onChange={e => setNewProduct({ ...newProduct, mrp: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">SP (Selling Price) *</label><input type="number" required min="0" value={newProduct.sp} onChange={e => setNewProduct({ ...newProduct, sp: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PV (Point Value) *</label><input type="number" required min="0" value={newProduct.pv} onChange={e => setNewProduct({ ...newProduct, pv: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Quantity *</label><input type="number" required min="0" value={newProduct.quantity} onChange={e => setNewProduct({ ...newProduct, quantity: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
              <select 
                value={newProduct.category} 
                onChange={e => setNewProduct({ ...newProduct, category: e.target.value, subcategory: "" })} 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" 
                required
              >
                <option value="">Select Category</option>{categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Subcategory</label>
              <select 
                value={newProduct.subcategory} 
                onChange={e => setNewProduct({ ...newProduct, subcategory: e.target.value })} 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                disabled={!newProduct.category}
              >
                <option value="">Select Subcategory</option>
                {subcategories.map((sub, idx) => (
                  <option key={idx} value={sub.name}>{sub.name}</option>
                ))}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Direct Commission (%)</label><input type="number" min="0" max="100" value={newProduct.directCommission} onChange={e => setNewProduct({ ...newProduct, directCommission: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Matrix Commission (%)</label><input type="number" min="0" max="100" value={newProduct.matrixCommission} onChange={e => setNewProduct({ ...newProduct, matrixCommission: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Generation Commission (%)</label><input type="number" min="0" max="100" value={newProduct.generationCommission} onChange={e => setNewProduct({ ...newProduct, generationCommission: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Image</label><input type="file" accept="image/*" onChange={e => setProductImageFile(e.target.files[0])} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />{productImageUploading && <span className="text-xs text-blue-600">Uploading...</span>}</div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="block text-xs font-medium text-slate-700 mb-1">Description</label><textarea value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" rows="2" /></div>
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end gap-2">
              <button type="button" onClick={resetProductForm} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
              <button type="submit" className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-blue-700">{editingProductId ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs sm:text-sm min-w-[800px]">
          <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Product</th><th className="px-4 py-3 text-left">Category</th><th className="px-4 py-3 text-left">Subcategory</th><th className="px-4 py-3 text-left">MRP</th><th className="px-4 py-3 text-left">SP</th><th className="px-4 py-3 text-left">PV</th><th className="px-4 py-3 text-left">Stock</th><th className="px-4 py-3 text-left">Commission</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
          <tbody>
            {products.length === 0 ? <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-500">No products</td></tr> : products.map(p => (
              <tr key={p._id} className="border-b hover:bg-slate-50">
                <td className="px-4 py-3"><div className="flex items-center gap-2"><img src={p.images?.[0] || "https://via.placeholder.com/40"} alt={p.name} className="w-8 h-8 rounded object-cover" /><div><p className="font-medium">{p.name}</p></div></div></td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">{categories.find(c => c._id === p.category)?.name || p.category || "General"}</span></td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">{p.subcategory || "None"}</span></td>
                <td className="px-4 py-3 font-medium">₹{p.mrp || p.price}</td>
                <td className="px-4 py-3 font-medium">₹{p.sp || p.price}</td>
                <td className="px-4 py-3 font-medium">{p.pv || p.price}</td>
                <td className="px-4 py-3"><span className={`font-medium ${p.quantity <= 10 ? "text-red-600" : "text-green-600"}`}>{p.quantity}</span></td>
                <td className="px-4 py-3 text-xs"><div>D: {p.directCommission || 0}%</div><div>M: {p.matrixCommission || 0}%</div><div>G: {p.generationCommission || 0}%</div></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs rounded-full ${p.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{p.isActive ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3"><div className="flex gap-1"><button onClick={() => handleEditProduct(p)} className="text-blue-600 hover:text-blue-800"><FiEdit size={14} /></button><button onClick={() => handleToggleProductStatus(p)} className={p.isActive ? "text-orange-600" : "text-green-600"}><FiToggleLeft size={14} /></button><button onClick={() => handleDeleteProduct(p._id)} className="text-red-600 hover:text-red-800"><FiTrash2 size={14} /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )};

  /* ─── Banners ─── */
  const renderBanners = () => (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-slate-800">Banner Management</h3>
        <button onClick={resetBannerForm} className="bg-slate-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-700">{editingBannerId ? "Cancel Edit" : "Clear"}</button>
      </div>
      <form onSubmit={handleBannerFormSubmit} className="bg-blue-50 p-4 sm:p-6 rounded-lg mb-5">
        <h4 className="font-bold text-slate-800 mb-3 text-sm">{editingBannerId ? "Edit Banner" : "Create Banner"}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <input type="text" value={bannerForm.title} onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="Banner Title" className="px-4 py-2 border-2 border-slate-300 rounded-lg text-sm" required />
          <input type="number" value={bannerForm.order} onChange={e => setBannerForm({ ...bannerForm, order: Number(e.target.value) })} placeholder="Order" className="px-4 py-2 border-2 border-slate-300 rounded-lg text-sm" />
          <textarea value={bannerForm.description} onChange={e => setBannerForm({ ...bannerForm, description: e.target.value })} placeholder="Description" className="px-4 py-2 border-2 border-slate-300 rounded-lg text-sm sm:col-span-2" rows="2" />
          <div className="flex items-center gap-2"><input type="checkbox" checked={bannerForm.isActive} onChange={e => setBannerForm({ ...bannerForm, isActive: e.target.checked })} id="b-active" /><label htmlFor="b-active" className="text-sm">Active</label></div>
          <div><input type="file" accept="image/*" onChange={e => setBannerImageFile(e.target.files[0] || null)} className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg text-sm" />{!bannerImageFile && bannerForm.image && <img src={bannerForm.image} alt="Banner" className="h-12 mt-2 rounded shadow" />}</div>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={bannerImageUploading} className="bg-green-600 text-white px-5 py-2 rounded font-semibold hover:bg-green-700 disabled:opacity-60 text-sm">{bannerImageUploading ? "Uploading..." : editingBannerId ? "Update" : "Create"}</button>
          <button type="button" onClick={resetBannerForm} className="bg-slate-600 text-white px-5 py-2 rounded font-semibold hover:bg-slate-700 text-sm">Cancel</button>
        </div>
      </form>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs sm:text-sm min-w-[500px]">
          <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Image</th><th className="px-4 py-3 text-left">Title</th><th className="px-4 py-3 text-left">Active</th><th className="px-4 py-3 text-left">Order</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
          <tbody>
            {banners.length > 0 ? banners.map(b => (
              <tr key={b._id} className="border-b hover:bg-slate-50">
                <td className="px-4 py-3"><img src={b.image} alt={b.title} className="h-10 rounded shadow" /></td>
                <td className="px-4 py-3 font-semibold">{b.title}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${b.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{b.isActive ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3">{b.order}</td>
                <td className="px-4 py-3"><div className="flex gap-2">
                  <button onClick={() => handleEditBanner(b)} className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700">Edit</button>
                  <button onClick={() => handleDeleteBanner(b._id)} className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700">Delete</button>
                </div></td>
              </tr>
            )) : <tr><td colSpan="5" className="px-6 py-8 text-center text-slate-500">No banners</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ─── Product Purchases ─── */
  const renderProductPurchases = () => {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="text-lg font-bold text-slate-800">Product Purchases</h3>
          <button onClick={fetchProductPurchases} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2">
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Total", val: productPurchases.length, color: "border-blue-600" },
            { label: "Pending", val: productPurchases.filter(p => p.deliveryStatus === "pending").length, color: "border-yellow-600" },
            { label: "Shipped", val: productPurchases.filter(p => p.deliveryStatus === "shipped").length, color: "border-purple-600" },
            { label: "Delivered", val: productPurchases.filter(p => p.deliveryStatus === "delivered").length, color: "border-green-600" },
          ].map(s => (
            <div key={s.label} className={`bg-white p-3 rounded-lg border-l-4 ${s.color} shadow-sm`}>
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className="text-xl font-bold text-slate-800">{s.val}</p>
            </div>
          ))}
        </div>

        {productPurchasesLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-500">
            Loading purchases...
          </div>
        ) : productPurchases.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-500">
            No product purchases found
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-xs sm:text-sm min-w-[800px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">PV</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Delivery</th>
                  <th className="px-4 py-3 text-left">Invoice</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {productPurchases.map((purchase) => (
                  <tr key={purchase._id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(purchase.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">
                        {purchase.userId?.name || "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {purchase.userId?.uniqueId || "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-700">
                        {purchase.productId?.name || "Unknown Product"}
                      </div>
                      <div className="text-xs text-slate-500">
                        ₹{(purchase.pricePerUnit || 0).toLocaleString("en-IN")}/unit
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">
                      {purchase.quantity || 1}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-600">
                      ₹{(purchase.totalAmount || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-600">
                      {purchase.pv || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        purchase.status === "completed" 
                          ? "bg-green-100 text-green-800" 
                          : purchase.status === "cancelled"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {(purchase.status || "pending").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        purchase.deliveryStatus === "delivered"
                          ? "bg-green-100 text-green-800"
                          : purchase.deliveryStatus === "shipped"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {(purchase.deliveryStatus || "pending").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-slate-700">
                        {purchase.invoiceNumber || (
                          <span className="text-slate-400 italic">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {purchase.invoiceNumber ? (
                        <button
                          onClick={() => handleViewInvoice(purchase._id)}
                          disabled={loadingInvoice}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1 text-xs font-semibold"
                        >
                          <FiEye /> View Invoice
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs italic">No Invoice</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /* ─── Categories ─── */
  const renderCategories = () => {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-800">Categories</h3>
          <button onClick={fetchCategories} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"><FiRefreshCw size={14} /> Refresh</button>
        </div>
        <form onSubmit={handleCategoryFormSubmit} className="bg-slate-50 p-4 rounded-lg mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div><label className="block text-xs font-medium mb-1">Name</label><input type="text" value={categoryForm.name} onChange={e => setCategoryForm(f => ({ ...f, name: e.target.value }))} required className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium mb-1">Description</label><input type="text" value={categoryForm.description} onChange={e => setCategoryForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium mb-1">Order</label><input type="number" value={categoryForm.order} onChange={e => setCategoryForm(f => ({ ...f, order: Number(e.target.value) }))} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium mb-1">Image</label><input type="file" accept="image/*" onChange={e => setCategoryImageFile(e.target.files[0])} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />{categoryImageUploading && <span className="text-xs text-blue-600">Uploading...</span>}</div>
            <div className="flex items-center gap-2 mt-4"><input type="checkbox" checked={categoryForm.isActive} onChange={e => setCategoryForm(f => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4" /><label className="text-sm">Active</label></div>
          </div>
          
          <div className="border-t border-slate-200 pt-4 mt-4">
            <h5 className="font-semibold text-sm mb-3 text-slate-700">Subcategories</h5>
            <div className="flex gap-2 mb-3">
              <input 
                type="text" 
                value={newSubcategory} 
                onChange={e => setNewSubcategory(e.target.value)} 
                placeholder="Add subcategory name" 
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSubcategory())}
              />
              <button type="button" onClick={addSubcategory} className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-700">Add</button>
            </div>
            
            {categoryForm.subcategories.length > 0 && (
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <div className="grid grid-cols-1 gap-2">
                  {categoryForm.subcategories.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                      <span className="text-sm">{sub.name}</span>
                      <button type="button" onClick={() => removeSubcategory(idx)} className="text-red-500 hover:text-red-700 text-sm">
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700" disabled={categoryImageUploading}>{editingCategoryId ? "Update" : "Add"}</button>
            {editingCategoryId && <button type="button" onClick={resetCategoryForm} className="bg-slate-300 text-slate-800 px-4 py-2 rounded-lg text-sm hover:bg-slate-400">Cancel</button>}
          </div>
        </form>

        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs sm:text-sm min-w-[600px]">
            <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Description</th><th className="px-4 py-3 text-left">Subcategories</th><th className="px-4 py-3 text-left">Image</th><th className="px-4 py-3 text-left">Active</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
            <tbody>
              {categories.length > 0 ? categories.map(c => (
                <tr key={c._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3 text-slate-500">{c.description}</td>
                  <td className="px-4 py-3">
                    {c.subcategories && c.subcategories.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {c.subcategories.map((sub, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">{sub.name}</span>
                        ))}
                      </div>
                    ) : <span className="text-slate-400 text-xs">None</span>}
                  </td>
                  <td className="px-4 py-3">{c.image && <img src={c.image} alt={c.name} className="h-8 rounded" />}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{c.isActive ? "Active" : "Inactive"}</span></td>
                  <td className="px-4 py-3"><div className="flex gap-2">
                    <button onClick={() => handleEditCategory(c)} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs hover:bg-blue-200">Edit</button>
                    <button onClick={() => handleDeleteCategory(c._id)} className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs hover:bg-red-200">Delete</button>
                  </div></td>
                </tr>
              )) : <tr><td colSpan="6" className="text-center py-6 text-slate-400">No categories</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /* ─── Contact ─── */
  const renderContact = () => (
    <div className="p-4 sm:p-6">
      <h3 className="text-lg font-bold text-slate-800 mb-5">
        {activeTab === "contact-list" ? "Contact List" : activeTab === "user-queries" ? "User Queries / Messages" : "Support Requests"}
      </h3>
      {!selectedContact ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs sm:text-sm min-w-[600px]">
            <thead className="bg-slate-100"><tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Subject</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Action</th></tr></thead>
            <tbody>
              {contacts.length > 0 ? contacts.map(c => (
                <tr key={c._id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3">{c.email}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{c.subject}</span></td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.status === "resolved" ? "bg-green-100 text-green-700" : c.status === "in-progress" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>{c.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><button onClick={() => setSelectedContact(c)} className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"><FiEye /></button></td>
                </tr>
              )) : <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No contact submissions</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-slate-50 p-4 sm:p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-bold text-slate-800">{selectedContact.name}</h4>
            <button onClick={() => setSelectedContact(null)} className="bg-slate-600 text-white px-3 py-1.5 rounded text-sm hover:bg-slate-700">← Back</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-white p-4 rounded border-l-4 border-blue-600"><p className="text-xs text-slate-500 mb-1">Contact</p><p className="font-bold text-slate-800">{selectedContact.name}</p><p className="text-sm">{selectedContact.email}</p><p className="text-sm">{selectedContact.phone}</p></div>
            <div className="bg-white p-4 rounded border-l-4 border-purple-600"><p className="text-xs text-slate-500 mb-1">Subject</p><p className="font-bold text-slate-800">{selectedContact.subject}</p><p className="text-sm text-slate-600">{new Date(selectedContact.createdAt).toLocaleDateString()}</p></div>
          </div>
          <div className="bg-white p-4 rounded border-l-4 border-orange-600 mb-4"><p className="text-xs text-slate-500 mb-1">Message</p><p className="text-slate-800">{selectedContact.message}</p></div>
          <div className="bg-white p-4 rounded border-l-4 border-green-600">
            <p className="font-semibold text-slate-800 mb-3 text-sm">Update</p>
            <select value={contactStatus} onChange={e => setContactStatus(e.target.value)} className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg text-sm mb-3">
              <option value="">Select status</option><option value="pending">Pending</option><option value="in-progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
            </select>
            <textarea value={contactResponse} onChange={e => setContactResponse(e.target.value)} placeholder="Response..." className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg text-sm mb-3" rows="3" />
            <div className="flex gap-3">
              <button onClick={() => handleUpdateContact(selectedContact._id)} className="bg-green-600 text-white px-5 py-2 rounded font-semibold hover:bg-green-700 text-sm flex items-center gap-2"><FiCheck /> Update</button>
              <button onClick={() => handleDeleteContact(selectedContact._id)} className="bg-red-600 text-white px-5 py-2 rounded font-semibold hover:bg-red-700 text-sm flex items-center gap-2"><FiTrash2 /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ════════════════════════════════════
     MAIN RENDER
  ════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* ── Top Header ── */}
      <header className="bg-white shadow-sm sticky top-0 z-30 px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Hamburger for mobile */}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100">
            <FiMenu size={20} />
          </button>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">Admin Dashboard</h1>
            <p className="text-xs text-slate-500 hidden sm:block">System management & oversight</p>
          </div>
        </div>
        {/* <div className="flex items-center gap-2">
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 bg-slate-800 text-white px-3 py-2 rounded-lg hover:bg-slate-900 transition text-xs sm:text-sm font-medium">
            <FiArrowLeft size={14} /> <span className="hidden sm:inline">Dashboard</span>
          </button>
        </div> */}
      </header>

      <div className="flex flex-1 relative overflow-hidden">
        {/* ── Sidebar Overlay (mobile) ── */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ── Sidebar ── */}
        <aside className={`
          fixed top-0 left-0 h-full z-30 w-64 bg-white shadow-xl flex flex-col transition-transform duration-300 ease-in-out
          lg:static lg:translate-x-0 lg:z-auto lg:shadow-none lg:h-auto lg:min-h-full lg:w-56 xl:w-60
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}>
          {/* Sidebar header (mobile) */}
          <div className="flex items-center justify-between px-4 py-3 border-b lg:hidden">
            <span className="font-bold text-slate-800">Menu</span>
            <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100"><FiX size={18} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 lg:pt-4" style={{ paddingTop: "60px" }}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 mb-2 hidden lg:block">Sections</p>
            <ul className="space-y-0.5">
              {SIDEBAR.map(section => {
                const isExpanded = expandedKeys[section.key];
                const isActiveSection = section.key === activeTab || (section.children.length === 0 && activeTab === section.key) || section.children.some(c => c.key === activeTab);
                return (
                  <li key={section.key}>
                    <button
                      onClick={() => {
                        if (section.children.length === 0) {
                          handleTabChange(section.key);
                        } else {
                          toggleExpand(section.key);
                          if (!isExpanded && section.children.length > 0) {
                            handleTabChange(section.children[0].key);
                          }
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActiveSection && section.children.length === 0 ? "bg-blue-600 text-white" : isActiveSection ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"}`}
                    >
                      <span className="flex items-center gap-2.5">{section.icon} {section.label}</span>
                      <span className="flex items-center">
                        {section.children.length > 0 && !isExpanded && (
                          <CountBadge count={section.children.reduce((sum, c) => sum + (sidebarCounts[c.key] || 0), 0)} active={false} />
                        )}
                        {section.children.length > 0 && (isExpanded ? <FiChevronDown size={14} className="ml-2" /> : <FiChevronRight size={14} className="ml-2" />)}
                      </span>
                    </button>
                    {section.children.length > 0 && isExpanded && (
                      <ul className="mt-0.5 ml-4 border-l-2 border-slate-200 pl-2 space-y-0.5">
                        {section.children.map(child => (
                          <li key={child.key}>
                            <button
                              onClick={() => handleTabChange(child.key)}
                              className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${activeTab === child.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"}`}
                            >
                              <span>{child.label}</span>
                              <CountBadge count={sidebarCounts[child.key]} active={activeTab === child.key} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-full">
            <div className="bg-white rounded-none lg:rounded-xl shadow-none lg:shadow-sm lg:m-4 lg:mb-6 overflow-hidden min-h-[calc(100vh-120px)]">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>

      {/* ── Bulk Approve Modal ── */}
      {bulkApproveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-base font-bold text-slate-800 mb-3">Confirm Bulk Approval</h3>
            <p className="text-slate-600 text-sm mb-5">Approve all {payments.filter(p => p.status === "pending").length} pending payments?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setBulkApproveConfirm(false)} className="bg-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-400">Cancel</button>
              <button onClick={() => {
                payments.filter(p => p.status === "pending").forEach(p => {
                  axios.put(`/payment/admin/${p._id}`, { status: "approved", adminNote: `₹${p.amount} transferred to LBBP wallet`, walletType: "lbbp" });
                });
                setBulkApproveConfirm(false); fetchPayments();
                alert(`${payments.filter(p => p.status === "pending").length} payments approved!`);
              }} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700">Approve All</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image Modal ── */}
      {imageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-base font-semibold text-slate-800">{modalImageTitle}</h3>
              <button onClick={handleCloseImageModal} className="text-slate-500 hover:text-slate-700"><FiX size={22} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-slate-50 flex items-center justify-center">
              <img src={modalImageUrl} alt={modalImageTitle} className="max-w-full h-auto rounded-lg shadow" onError={e => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='120' viewBox='0 0 200 120'%3E%3Crect width='200' height='120' fill='%23f3f4f6'/%3E%3Ctext x='100' y='60' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='14' fill='%236b7280'%3EImage not available%3C/text%3E%3C/svg%3E"; }} />
            </div>
            <div className="p-3 border-t flex justify-end gap-2">
              <button onClick={() => window.open(modalImageUrl, "_blank")} className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Open Tab</button>
              <button onClick={handleCloseImageModal} className="bg-slate-600 text-white px-4 py-2 rounded text-sm hover:bg-slate-700">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Modal ── */}
      {showInvoiceModal && invoiceData && (
        <Invoice
          invoice={invoiceData}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoiceData(null);
          }}
          onPrint={handlePrintInvoice}
        />
      )}
    </div>
  );
};

export default AdminDashboard;